import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import { getDbInstance, getStorageInstance, getAuthInstance } from './firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from "firebase/firestore";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Extension } from '@tiptap/core';
import TextStyle from '@tiptap/extension-text-style';
import History from '@tiptap/extension-history';
import Image from '@tiptap/extension-image';
import ResizeImage from 'tiptap-extension-resize-image';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import DeltaForce from './delta-force';
import Fitness from './fitness';

// Add this at the top of the file, before PlannerView:
function formatTimeForInput(date) {
  return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}

const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return { types: ['textStyle'] };
  },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        fontSize: {
          default: null,
          parseHTML: element => element.style.fontSize.replace(/['"]+/g, ''),
          renderHTML: attributes => {
            if (!attributes.fontSize) return {};
            return { style: `font-size: ${attributes.fontSize}` };
          },
        },
      },
    }];
  },
});

const TiptapEditor = ({ content, onContentChange, isMobile, onEditorReady }) => {
    const editor = useEditor({
        extensions: [
          StarterKit.configure({
            dropcursor: false,
            history: false,
          }),
          TextStyle.configure(),
          FontSize,
          History,
          Image,
          ResizeImage,
          Table.configure({ resizable: true }),
          TableRow,
          TableHeader,
          TableCell,
        ],
        content: content,
        onUpdate: ({ editor }) => {
            onContentChange(editor.getHTML());
        },
    });

    // Notify parent component when editor is ready
    useEffect(() => {
        if (editor && onEditorReady) {
            onEditorReady(editor);
        }
    }, [editor, onEditorReady]);

    // Handle paste image event
    useEffect(() => {
        if (!editor) return;
        const handlePaste = async (event) => {
            const items = event.clipboardData?.items;
            if (!items) return;
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.type.indexOf('image') !== -1) {
                    event.preventDefault();
                    const file = item.getAsFile();
                    if (!file) return;
                    // Upload to Firebase Storage
                    const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
                    const storageInstance = getStorageInstance();
                    if (!storageInstance) return;
                    const imageRef = ref(storageInstance, `notes_images/${Date.now()}_${file.name}`);
                    await uploadBytes(imageRef, file);
                    const url = await getDownloadURL(imageRef);
                    // Insert image at current selection
                    editor.chain().focus().setImage({ src: url }).run();
                }
            }
        };
        const proseMirror = editor?.view?.dom;
        if (proseMirror) {
            proseMirror.addEventListener('paste', handlePaste);
        }
        return () => {
            if (proseMirror) {
                proseMirror.removeEventListener('paste', handlePaste);
            }
        };
    }, [editor]);

    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content);
        }
    }, [content, editor]);

    return (
        <div className="tiptap-editor-root" style={{ width: '100%' }}>
          <EditorContent editor={editor} className="tiptap-editor-content"/>
        </div>
    );
};

// Short bio shown on load and by `whoami` — one source of truth.
const BIO = `Rohit Rao — ML performance engineer at Apex Compute.
Quantization, MLIR extraction pipelines, and post-training
alignment for transformer inference on edge silicon.`;

const directories = {
  '~': {
    type: 'directory',
    content: 'Welcome to my personal website! Type "ls" to see available pages.',
    children: ['about', 'experience', 'projects', 'contact', 'help']
  },
  '~/about': {
    type: 'directory',
    title: 'About Me',
    content: `ML performance and accelerators.`
  },
  '~/experience': {
      type: 'directory',
      title: 'Experience & Research',
      content: `Machine Learning Performance Engineer - Apex Compute | Aug 2025 - Present
   Optimizing transformer model performance through quantization, MLIR extraction pipelines, and post-training alignment for edge inference.

Deep Learning Engineer Intern - Texas Instruments | May 2025 - Aug 2025
   Deployed and optimized vision and LLM models on TI SoCs, including post-training quantization and compute graph optimization for the TIDL inference stack.

3D Vision Research - Ming-Hsuan Yang Lab, UC Merced | Jan 2024 - May 2024
   Contributed to 3D human-object interaction reconstruction using diffusion-based multiview generation, HMR, and NeRF/Gaussian Splatting.

Computer Vision Research - PADSYS Lab, UC Merced | Aug 2023 - May 2024
   Benchmarked object detection models across HPC interconnect protocols using distributed training with PyTorch DDP and SLURM.

Robotics Research Assistant - UC Merced | Aug 2022 - Aug 2023
   Prototyped autonomous IoT agricultural robots for soil data collection, contributing to navigation algorithms and sensor integration.`
  },
  '~/projects': {
    type: 'directory',
    content: 'My featured projects:',
    children: ['gemstack', 'custom-chatbots', 'autoeasy', 'guitar-chord-classifier', 'hoverbot']
  },
  '~/projects/autoeasy': {
    type: 'directory',
    title: 'AutoEasy - Vision-powered e-Commerce Platform for Easy Car Listings',
    content: `Aug 2024 - Jan 2025
- Developed a custom CNN model and benchmarked against an 18-layer ResNet for car detection in user-uploaded images, enabling car specification autofill for listings.
- Processed the Stanford Cars dataset and successfully detected 196 distinct car classes with bounding box accuracy of 93%.
- Utilized a Vite-React frontend and GCP backend for database management to design a full-stack platform for users to buy, sell, and access car listings with accident history reports. [All Code]
- Utilized: Python, PyTorch, ResNet18, Flask, REST, Vite-React, GCP Database, Docker`
  },
  '~/projects/gemstack': {
    type: 'directory',
    title: 'Autonomous Vehicle RL Navigation (GEMStack)',
    content: `Jan 2025 - May 2025
- Implemented Q-Learning and SARSA control policies within the GEMStack software framework to enable autonomous path planning and obstacle avoidance in simulated urban environments.
- Designed custom reward functions to optimize agent behavior, balancing sparse rewards (goal completion) with dense penalties (collision risk, lane deviation, and jerk), significantly improving convergence speed.
- Conducted comparative analysis of on-policy (SARSA) vs. off-policy (Q-Learning) performance, evaluating trajectory smoothness and safety margins across dynamic intersection scenarios.`
  },
  '~/projects/custom-chatbots': {
    type: 'directory',
    title: 'Custom Chatbots - Parameter Efficient Fine-Tuning for LLMs, Consulting Sweep AI',
    content: `Jan 2024 - May 2024
- Utilized SoTA fine-tuning approaches like LoRA through utilizing parameter efficient fine-tuning (PEFT).
- Developed a fine-tuning pipeline requested by using LLAMA-2 7B for user customized chatbots.
- Implemented a Flask RESTful API to handle new model creation, automate training, and process user data.
- API endpoints allow clients to begin training tasks, inference with their custom models and access checkpoint files from fine-tuning. [All Code]
- Utilized: Python, PyTorch, HuggingFace Transformers, Flask, REST, HTML/CSS/JS, Docker`
  },
  '~/projects/guitar-chord-classifier': {
      type: 'directory',
      title: 'Guitar Chord Classifier - Detects and Classifies Guitar Chords',
      content: `Jan 2024
- Trained and validated convolutional neural network (MobileNet SSD V2) to visually detect and identify 5 unique guitar chords. You can find a video demo here: https://www.linkedin.com/feed/update/urn:li:activity:7155348931055357952/.
- Experimented with other CNN architectures such as YOLO and Faster-RCNN but resorted to a single-shot detector as it performed exponentially better with very little data.
- Data for the model was self-curated, through collecting and hand-labeling a multitude of web-cam photos for each class.
- Utilized: Python, TensorFlow, Data Validation`
  },
  '~/projects/hoverbot': {
    type: 'directory',
    title: 'HoverBot - GPS Delivery Rover',
    content: `May 2022 - July 2022
- Leveraged hoverboard motors to construct a rover paired with a NEO-6M GPS for heavy weight deliveries.
- GPS and magnetometer readings used to calculate heading and precise direction sense for accurate navigation. The rover is equipped with an ultrasonic sensor-based lane correction system capable of avoiding obstacles.
- Flashed a modified firmware onto the motor controller using STM32CubeProgrammer.
- Programmed rover movement and sensors using Arduino Mega 2560 controller running embedded C scripts. Utilized I2C and other serial protocols to interact with the motors and all the sensors.
- Medium Article: https://medium.com/@rohitrrao2021/how-to-build-your-own-delivery-robot-edff353ba1cc
- Video Demonstration: https://youtu.be/_90CReOinwE
- Utilized: C, C++, Arduino IDE, Adafruit, STM32CubeProgrammer`
  },
  '~/contact': {
    type: 'directory',
    title: 'Contact Information',
    content: `Get in touch with me:

   Email: rohitrrao2021@gmail.com
   Phone: +1 408-300-4823
   GitHub: github.com/Rohit-R-Rao
   Website: rohitrrao.com
   LinkedIn: https://www.linkedin.com/in/rohit--rao/

I'm always open to discussing new opportunities, collaborations, or just chatting about technology!`,
    image: 'https://via.placeholder.com/400x250/f39c12/ffffff?text=Contact+Info'
  },
  '~/help': {
    type: 'directory',
    title: 'Available Commands',
    content: `Navigation
  ls [-a -l -1] [path]     List directory contents
  cd <dir>                 Change directory (.. to go up, ~ for home)
  pwd                      Print working directory
  tree [dir]               Print the directory tree

Reading files
  cat <file>               Print a file
  head [-n N] <file>       First N lines (default 10)
  tail [-n N] <file>       Last N lines (default 10)
  wc [-l -w -c] <file>     Count lines, words, bytes
  file <path>              Identify a path
  stat <path>              Show path details

Searching
  grep [-i -n -r] <pattern> [path]      Search file contents
  find [path] [-name PAT] [-type f|d]   Search for paths

Shell
  history                  Commands run this session
  clear                    Clear the screen
  help                     This message
  gui                      Switch to the normal GUI view
  theme                    Toggle dark and light

This shell is read-only. Commands that write, install or reach the
network (rm, mkdir, curl, git, ...) are recognised but disabled.`,
    image: 'https://via.placeholder.com/400x250/34495e/ffffff?text=Help+Guide'
  }
};

// ---------------------------------------------------------------------------
// A small read-only filesystem.
//
// `directories` above stays the authoring surface (and is what the GUI view
// reads). This derives a real tree from it so the shell can treat files and
// directories as genuinely different things: a node has children or it has
// content, never both.
// ---------------------------------------------------------------------------

// Content's last revision. Bump when you edit `directories`.
const SITE_MTIME = new Date('2026-05-26T16:16:09');

function buildNode(path) {
  const entry = directories[path];
  if (!entry.children) {
    return { type: 'file', content: entry.content || '', mtime: SITE_MTIME };
  }
  const children = {};
  for (const child of entry.children) {
    children[child] = buildNode(path === '~' ? `~/${child}` : `${path}/${child}`);
  }
  return { type: 'dir', children, mtime: SITE_MTIME };
}

const FS = buildNode('~');

const segments = p => p.split('/').filter(Boolean);
const baseName = p => segments(p).pop() || '~';

// Resolve `target` against `cwd` into an absolute '~/a/b' path.
function resolvePath(cwd, target) {
  if (!target) return '~';
  let parts;
  if (target === '~' || target.startsWith('~/')) parts = segments(target.slice(1));
  else if (target.startsWith('/')) parts = segments(target);
  else parts = segments(cwd).slice(1).concat(segments(target));

  const out = [];
  for (const part of parts) {
    if (part === '.') continue;
    if (part === '..') out.pop();
    else out.push(part);
  }
  return out.length ? `~/${out.join('/')}` : '~';
}

function lookup(path) {
  let node = FS;
  for (const part of segments(path).slice(1)) {
    if (node.type !== 'dir' || !node.children[part]) return null;
    node = node.children[part];
  }
  return node;
}

// Split a command line into argv, honouring single and double quotes so that
// `find . -name "*bot*"` and `grep 'machine learning' about` behave.
function tokenize(line) {
  const tokens = [];
  let current = '';
  let quote = null;
  let quoted = false;
  for (const ch of line) {
    if (quote) {
      if (ch === quote) quote = null;
      else current += ch;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
      quoted = true;
    } else if (/\s/.test(ch)) {
      if (quoted || current) { tokens.push(current); current = ''; quoted = false; }
    } else {
      current += ch;
    }
  }
  if (quoted || current) tokens.push(current);
  return tokens;
}

// Minimal getopt: handles -l, -la, -n 5 and -n5. `numericCount` additionally
// reads a bare -5 as a count, which only head/tail want — for ls, -1 is a flag.
function parseArgs(args, valueFlags = [], numericCount = false) {
  const flags = new Set();
  const values = {};
  const operands = [];
  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (arg === '--') { operands.push(...args.slice(i + 1)); break; }
    if (arg.length < 2 || !arg.startsWith('-')) { operands.push(arg); continue; }
    const body = arg.slice(1);
    if (numericCount && /^\d+$/.test(body)) { values['#'] = body; continue; }
    for (let j = 0; j < body.length; j++) {
      const ch = body[j];
      if (valueFlags.includes(ch)) {
        values[ch] = body.slice(j + 1) || args[++i];
        break;
      }
      flags.add(ch);
    }
  }
  return { flags, values, operands };
}

const nodeSize = node => (node.type === 'dir' ? 4096 : node.content.length);
const decorate = (name, node) => `${name}${node.type === 'dir' ? '/' : ''}`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtDate(d) {
  const pad = n => String(n).padStart(2, '0');
  return `${MONTHS[d.getMonth()]} ${String(d.getDate()).padStart(2, ' ')} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Nothing here is writable, so the mode bits say so.
function longEntry(name, node) {
  const isDir = node.type === 'dir';
  const perms = isDir ? 'dr-xr-xr-x' : '-r--r--r--';
  const links = String(isDir ? Object.keys(node.children).length + 2 : 1).padStart(2, ' ');
  const size = String(nodeSize(node)).padStart(5, ' ');
  return `${perms} ${links} rohit staff ${size} ${fmtDate(node.mtime)} ${decorate(name, node)}`;
}

function cmdLs(cwd, args) {
  const { flags, operands } = parseArgs(args);
  const targets = operands.length ? operands : ['.'];
  const blocks = [];

  for (const target of targets) {
    const path = resolvePath(cwd, target);
    const node = lookup(path);
    if (!node) { blocks.push(`ls: ${target}: No such file or directory`); continue; }

    const header = targets.length > 1 ? `${target}:\n` : '';
    if (node.type === 'file') {
      blocks.push(header + (flags.has('l') ? longEntry(baseName(path), node) : baseName(path)));
      continue;
    }

    let names = Object.keys(node.children).sort();
    if (flags.has('a')) names = ['.', '..', ...names];
    const nodeFor = name => {
      if (name === '.') return node;
      if (name === '..') return lookup(resolvePath(path, '..')) || node;
      return node.children[name];
    };

    if (flags.has('l')) {
      const blocks512 = names.reduce((sum, name) => sum + Math.ceil(nodeSize(nodeFor(name)) / 512), 0);
      const rows = names.map(name => longEntry(name, nodeFor(name)));
      blocks.push(`${header}total ${blocks512}\n${rows.join('\n')}`);
    } else {
      const cells = names.map(name => decorate(name, nodeFor(name)));
      blocks.push(header + cells.join(flags.has('1') ? '\n' : '    '));
    }
  }
  return blocks.join('\n\n');
}

function cmdTree(cwd, args) {
  const { operands } = parseArgs(args);
  const label = operands[0] || '.';
  const node = lookup(resolvePath(cwd, label));
  if (!node) return `tree: ${label}: No such file or directory`;
  if (node.type === 'file') return label;

  const lines = [label];
  let dirs = 0;
  let files = 0;
  const walk = (dir, prefix) => {
    const names = Object.keys(dir.children).sort();
    names.forEach((name, i) => {
      const last = i === names.length - 1;
      const child = dir.children[name];
      lines.push(`${prefix}${last ? '└── ' : '├── '}${decorate(name, child)}`);
      if (child.type === 'dir') { dirs++; walk(child, `${prefix}${last ? '    ' : '│   '}`); }
      else files++;
    });
  };
  walk(node, '');
  lines.push('', `${dirs} ${dirs === 1 ? 'directory' : 'directories'}, ${files} ${files === 1 ? 'file' : 'files'}`);
  return lines.join('\n');
}

// Shared resolution for the commands that read file contents.
function readTarget(cwd, target, name) {
  const node = lookup(resolvePath(cwd, target));
  if (!node) return { error: `${name}: ${target}: No such file or directory` };
  if (node.type === 'dir') return { error: `${name}: ${target}: Is a directory` };
  return { node };
}

function cmdCat(cwd, args, name = 'cat') {
  const { operands } = parseArgs(args);
  if (!operands.length) return `${name}: missing operand`;
  return operands.map(target => {
    const { error, node } = readTarget(cwd, target, name);
    return error || node.content;
  }).join('\n');
}

function cmdHeadTail(cwd, args, name) {
  const { values, operands } = parseArgs(args, ['n'], true);
  const count = parseInt(values.n ?? values['#'] ?? '10', 10);
  if (!Number.isFinite(count) || count < 0) return `${name}: invalid number of lines`;
  if (!operands.length) return `${name}: missing operand`;

  return operands.map(target => {
    const { error, node } = readTarget(cwd, target, name);
    if (error) return error;
    const lines = node.content.split('\n');
    const slice = name === 'head' ? lines.slice(0, count) : lines.slice(Math.max(0, lines.length - count));
    const header = operands.length > 1 ? `==> ${target} <==\n` : '';
    return header + slice.join('\n');
  }).join('\n\n');
}

function cmdWc(cwd, args) {
  const { flags, operands } = parseArgs(args);
  if (!operands.length) return 'wc: missing operand';
  const show = flags.has('l') || flags.has('w') || flags.has('c');
  const row = (l, w, c, label) => {
    const cols = [];
    if (!show || flags.has('l')) cols.push(String(l).padStart(4, ' '));
    if (!show || flags.has('w')) cols.push(String(w).padStart(5, ' '));
    if (!show || flags.has('c')) cols.push(String(c).padStart(6, ' '));
    return `${cols.join(' ')} ${label}`;
  };

  let totals = [0, 0, 0];
  const rows = operands.map(target => {
    const { error, node } = readTarget(cwd, target, 'wc');
    if (error) return error;
    const counts = [
      node.content.split('\n').length,
      node.content.split(/\s+/).filter(Boolean).length,
      node.content.length,
    ];
    totals = totals.map((t, i) => t + counts[i]);
    return row(...counts, target);
  });
  if (operands.length > 1) rows.push(row(...totals, 'total'));
  return rows.join('\n');
}

function cmdFile(cwd, args) {
  const { operands } = parseArgs(args);
  if (!operands.length) return 'file: missing operand';
  return operands.map(target => {
    const node = lookup(resolvePath(cwd, target));
    if (!node) return `${target}: cannot open (No such file or directory)`;
    return `${target}: ${node.type === 'dir' ? 'directory' : 'ASCII text'}`;
  }).join('\n');
}

function cmdStat(cwd, args) {
  const { operands } = parseArgs(args);
  if (!operands.length) return 'stat: missing operand';
  return operands.map(target => {
    const path = resolvePath(cwd, target);
    const node = lookup(path);
    if (!node) return `stat: cannot stat '${target}': No such file or directory`;
    const isDir = node.type === 'dir';
    return [
      `  File: ${path.replace('~', '/home/rohit')}`,
      `  Size: ${nodeSize(node)}   Type: ${isDir ? 'directory' : 'regular file'}`,
      `Access: (${isDir ? '0555/dr-xr-xr-x' : '0444/-r--r--r--'})  Uid: (1000/rohit)  Gid: (1000/staff)`,
      `Modify: ${node.mtime.toISOString().slice(0, 19).replace('T', ' ')}`,
    ].join('\n');
  }).join('\n\n');
}

// Collect every file under a directory, depth first.
function collectFiles(path, node, out) {
  if (node.type === 'file') { out.push([path, node]); return; }
  for (const name of Object.keys(node.children).sort()) {
    collectFiles(`${path}/${name}`, node.children[name], out);
  }
}

// Display a resolved path the way the user referred to its root.
const relabel = (path, rootPath, rootLabel) =>
  (path === rootPath ? rootLabel : `${rootLabel}/${path.slice(rootPath.length + 1)}`).replace(/^\.\//, '');

function cmdGrep(cwd, args) {
  const { flags, operands } = parseArgs(args);
  if (!operands.length) return 'usage: grep [-inr] pattern [path ...]';
  const [pattern, ...rest] = operands;
  const targets = rest.length ? rest : ['.'];

  let re;
  try { re = new RegExp(pattern, flags.has('i') ? 'i' : ''); }
  catch { return `grep: ${pattern}: invalid regular expression`; }

  const recursive = flags.has('r') || flags.has('R');
  const errors = [];
  const files = [];
  for (const target of targets) {
    const path = resolvePath(cwd, target);
    const node = lookup(path);
    if (!node) { errors.push(`grep: ${target}: No such file or directory`); continue; }
    if (node.type === 'dir' && !recursive) { errors.push(`grep: ${target}: Is a directory`); continue; }
    const found = [];
    collectFiles(path, node, found);
    files.push(...found.map(([p, n]) => [relabel(p, path, target), n]));
  }

  const hits = [];
  for (const [label, node] of files) {
    node.content.split('\n').forEach((line, i) => {
      if (!re.test(line)) return;
      const prefix = (files.length > 1 ? `${label}:` : '') + (flags.has('n') ? `${i + 1}:` : '');
      hits.push(prefix + line);
    });
  }
  return [...errors, ...hits].join('\n');
}

const globToRegExp = glob =>
  new RegExp(`^${glob.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*').replace(/\?/g, '.')}$`);

function cmdFind(cwd, args) {
  const operands = [];
  let namePattern = null;
  let typeFilter = null;
  for (let i = 0; i < args.length; i++) {
    if (args[i] === '-name') namePattern = args[++i];
    else if (args[i] === '-type') typeFilter = args[++i];
    else if (!args[i].startsWith('-')) operands.push(args[i]);
  }

  const label = operands[0] || '.';
  const rootPath = resolvePath(cwd, label);
  const root = lookup(rootPath);
  if (!root) return `find: '${label}': No such file or directory`;
  const re = namePattern ? globToRegExp(namePattern) : null;

  const matches = [];
  const walk = (path, node) => {
    const wantedType = !typeFilter || (typeFilter === 'd' ? node.type === 'dir' : node.type === 'file');
    if (wantedType && (!re || re.test(baseName(path)))) matches.push(relabel(path, rootPath, label));
    if (node.type !== 'dir') return;
    for (const name of Object.keys(node.children).sort()) walk(`${path}/${name}`, node.children[name]);
  };
  walk(rootPath, root);
  return matches.join('\n');
}

const COMMANDS = [
  'cat', 'cd', 'clear', 'date', 'echo', 'file', 'find', 'grep', 'gui', 'head', 'help',
  'history', 'hostname', 'less', 'll', 'la', 'ls', 'man', 'more', 'pwd', 'stat', 'tail',
  'theme', 'tree', 'uname', 'uptime', 'wc', 'whoami', 'ro init',
];

// Doors into Delta Force (src/delta-force). Deliberately not in COMMANDS or help.
const DELTA_FORCE_ALIASES = new Set(['df', 'delta-force', 'delta force', 'deltaforce']);

// Commands whose arguments are paths, so Tab should complete against the tree.
const PATH_COMMANDS = new Set(['cd', 'cat', 'less', 'more', 'head', 'tail', 'wc', 'file', 'stat', 'ls', 'll', 'la', 'tree', 'find', 'grep']);

// Complete `partial` (which may contain slashes) against the filesystem.
function completePath(cwd, partial) {
  const cut = partial.lastIndexOf('/');
  const dirPart = cut === -1 ? '' : partial.slice(0, cut + 1);
  const stem = cut === -1 ? partial : partial.slice(cut + 1);
  const dir = lookup(resolvePath(cwd, dirPart || '.'));
  if (!dir || dir.type !== 'dir') return [];
  return Object.keys(dir.children)
    .filter(name => name.startsWith(stem))
    .map(name => dirPart + name + (dir.children[name].type === 'dir' ? '/' : ''));
}

// Real commands that this shell deliberately does not implement. Listing them
// means `rm -rf /` gets an honest answer instead of "command not found".
const DISABLED_COMMANDS = new Set([
  'rm', 'rmdir', 'mv', 'cp', 'mkdir', 'touch', 'ln', 'chmod', 'chown', 'dd', 'truncate', 'sed', 'awk',
  'du', 'ps', 'top', 'htop', 'free', 'kill', 'killall', 'mount', 'umount', 'lsof', 'dmesg',
  'sudo', 'su', 'passwd', 'useradd', 'chsh', 'export', 'source', 'alias',
  'vim', 'vi', 'nano', 'emacs', 'ed', 'code',
  'apt', 'apt-get', 'yum', 'dnf', 'pacman', 'brew', 'npm', 'yarn', 'pnpm', 'pip',
  'curl', 'wget', 'ssh', 'scp', 'rsync', 'ping', 'netstat', 'ifconfig', 'ip', 'dig', 'nslookup', 'nc', 'telnet',
  'git', 'make', 'gcc', 'g++', 'python', 'python3', 'node', 'docker', 'kubectl',
  'systemctl', 'service', 'crontab', 'tar', 'zip', 'unzip', 'gzip', 'reboot', 'shutdown', 'poweroff',
]);

// Cycled as ghost text at the prompt so an arriving visitor can see what this
// shell actually does without having to guess a command.
const DEMO_COMMANDS = [
  'whoami',
  'ls -al',
  'cat about',
  'cd projects',
  'tree',
  'grep -ri pytorch .',
  'help',
  'gui',
];

// Must match the .ghost-command animation duration in App.css.
const GHOST_INTERVAL_MS = 1900;

// Someone who types nothing at all is told, not left staring at a prompt.
const IDLE_REDIRECT_MS = 30000;

function App() {
  const [currentDirectory, setCurrentDirectory] = useState('~');
  const [commandHistory, setCommandHistory] = useState([]);
  const [currentCommand, setCurrentCommand] = useState('');
  const [commandIndex, setCommandIndex] = useState(0);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isTerminalMode, setIsTerminalMode] = useState(true);
  // Broker portals send people back to the site root with ?df=...; land them in Delta Force, not the prompt.
  const [appState, setAppState] = useState(() =>
    new URLSearchParams(window.location.search).has('df') ? 'delta-force' : 'main'
  ); // main, shutdown, bios, notes, planner, fitness, delta-force
  const terminalRef = useRef(null);
  const inputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [initialNoteId, setInitialNoteId] = useState(null);
  const [ghostIndex, setGhostIndex] = useState(0);
  // The reel stops once a command has been run, but it comes back if someone
  // returns to the terminal later; the redirect, once spent, never re-fires.
  const [hasRunCommand, setHasRunCommand] = useState(false);
  const [redirectArmed, setRedirectArmed] = useState(true);
  const [activityTick, setActivityTick] = useState(0);

  useEffect(() => {
    // Only check auth state when we're in notes app or bios
    if (appState === 'notes' || appState === 'bios') {
      try {
        const authInstance = getAuthInstance();
        if (authInstance) {
          console.log('Setting up auth state listener...');
          const unsubscribe = onAuthStateChanged(authInstance, (firebaseUser) => {
            console.log('Auth state changed:', firebaseUser ? firebaseUser.email : 'No user');
            if (firebaseUser) {
              setUser(firebaseUser);
              setAuthChecked(true);
            } else {
              // No user signed in, mark as checked
              setAuthChecked(true);
            }
          });

          return () => {
            console.log('Cleaning up auth state listener...');
            unsubscribe();
          };
        } else {
          console.log('No auth instance available');
          setAuthChecked(true);
        }
      } catch (error) {
        console.log('Auth not available:', error);
        setAuthChecked(true);
      }
    } else {
      // For portfolio/terminal view, don't check auth
      setAuthChecked(true);
    }
  }, [appState]);

  const handleLoginSuccess = () => {
    setAppState('planner');
  };

  const executeCommand = (command) => {
    const trimmedCommand = command.trim();

    if ((trimmedCommand === 'init' || trimmedCommand === 'ro init') && appState !== 'bios' && appState !== 'notes') {
      setAppState('bios');
      setCommandHistory([]);
      setCurrentCommand('');
      setHasRunCommand(true);
      setRedirectArmed(false);
      return;
    }

    if (DELTA_FORCE_ALIASES.has(trimmedCommand.toLowerCase())) {
      setAppState('delta-force');
      setCurrentCommand('');
      setHasRunCommand(true);
      setRedirectArmed(false);
      return;
    }

    const parts = tokenize(trimmedCommand);
    const cmd = parts[0] || '';
    const args = parts.slice(1);

    let output = '';
    let newDirectory = currentDirectory;
    let recognised = true;

    switch (cmd) {
      case '':
        break;

      case 'ls':
        output = cmdLs(currentDirectory, args);
        break;

      case 'll':
        output = cmdLs(currentDirectory, ['-l', ...args]);
        break;

      case 'la':
        output = cmdLs(currentDirectory, ['-la', ...args]);
        break;

      case 'cd': {
        const { operands } = parseArgs(args);
        const target = operands[0] || '~';
        const path = resolvePath(currentDirectory, target);
        const node = lookup(path);
        if (!node) output = `cd: ${target}: No such file or directory`;
        else if (node.type !== 'dir') output = `cd: ${target}: Not a directory`;
        else newDirectory = path;
        break;
      }

      case 'pwd':
        output = currentDirectory.replace('~', '/home/rohit');
        break;

      case 'tree':
        output = cmdTree(currentDirectory, args);
        break;

      case 'cat':
      case 'less':
      case 'more':
        output = cmdCat(currentDirectory, args, cmd);
        break;

      case 'head':
      case 'tail':
        output = cmdHeadTail(currentDirectory, args, cmd);
        break;

      case 'wc':
        output = cmdWc(currentDirectory, args);
        break;

      case 'file':
        output = cmdFile(currentDirectory, args);
        break;

      case 'stat':
        output = cmdStat(currentDirectory, args);
        break;

      case 'grep':
        output = cmdGrep(currentDirectory, args);
        break;

      case 'find':
        output = cmdFind(currentDirectory, args);
        break;

      case 'whoami':
        output = BIO;
        break;

      case 'echo':
        output = args.join(' ');
        break;

      case 'date':
        output = new Date().toString();
        break;

      case 'uname':
        output = args.includes('-a') ? 'RohitOS 1.0.0 Portfolio-Terminal x86_64 GNU/Linux' : 'RohitOS';
        break;

      case 'hostname':
        output = 'rohit-portfolio';
        break;

      case 'uptime':
        output = 'up since you loaded this page';
        break;

      case 'history':
        output = commandHistory
          .filter(entry => entry.command)
          .map((entry, idx) => `  ${idx + 1}  ${entry.command}`)
          .join('\n') || 'No commands in history.';
        break;

      case 'man':
        output = args.length
          ? `man: no manual entry for '${args[0]}' — try 'help'`
          : 'What manual page do you want? Try `help`.';
        break;

      case 'help':
        output = directories['~/help'].content;
        break;

      case 'clear':
        setCommandHistory([]);
        setHasRunCommand(true);
        setRedirectArmed(false);
        return;

      case 'gui':
        setIsTerminalMode(false);
        setHasRunCommand(true);
        setRedirectArmed(false);
        return;

      case 'theme':
        setIsDarkMode(prev => !prev);
        setHasRunCommand(true);
        setRedirectArmed(false);
        return;

      default:
        recognised = false;
        output = DISABLED_COMMANDS.has(cmd)
          ? `${cmd}: disabled — this shell is read-only`
          : `${cmd}: command not found. Type 'help' for available commands.`;
    }

    const newHistory = [...commandHistory, {
      command: trimmedCommand,
      output,
      directory: currentDirectory,
      timestamp: new Date().toLocaleTimeString()
    }];

    if (cmd && recognised) {
      setHasRunCommand(true);
      setRedirectArmed(false);
    }
    setCommandHistory(newHistory);
    setCurrentDirectory(newDirectory);
    setCommandIndex(newHistory.length);
  };

  const handleAutocomplete = () => {
    const parts = currentCommand.trim().split(' ');
    const partial = parts[parts.length - 1];

    const matches = parts.length === 1
      ? COMMANDS.filter(opt => opt.startsWith(partial))
      : PATH_COMMANDS.has(parts[0]) ? completePath(currentDirectory, partial) : [];

    if (matches.length === 1) {
      const completed = parts.slice(0, -1).concat(matches[0]).join(' ');
      setCurrentCommand(completed.endsWith('/') ? completed : `${completed} `);
    } else if (matches.length > 1) {
      const newHistory = [...commandHistory, {
        command: currentCommand,
        output: matches.join('  '),
        directory: currentDirectory,
        timestamp: new Date().toLocaleTimeString()
      }];
      setCommandHistory(newHistory);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      handleAutocomplete();
      return;
    }
    
    if (e.key === 'Enter') {
      if (appState === 'shutdown') return;
      
      executeCommand(currentCommand);
      setCurrentCommand('');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandIndex > 0) {
        const prevCommand = commandHistory[commandIndex - 1]?.command || '';
        setCurrentCommand(prevCommand);
        setCommandIndex(commandIndex - 1);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (commandIndex < commandHistory.length) {
        const nextCommand = commandHistory[commandIndex]?.command || '';
        setCurrentCommand(nextCommand);
        setCommandIndex(commandIndex + 1);
      }
    }
  };

  const showInput = appState === 'main';
  // Ghost text only while the prompt is empty and nothing has been run yet.
  const showGhost = showInput && isTerminalMode && !hasRunCommand && currentCommand === '';

  useEffect(() => {
    if (!showGhost) return;
    const reel = setInterval(
      () => setGhostIndex(i => (i + 1) % DEMO_COMMANDS.length),
      GHOST_INTERVAL_MS
    );
    return () => clearInterval(reel);
  }, [showGhost]);

  // Typing restarts the countdown; running a command disarms it permanently.
  // Firing also disarms, so returning to the terminal by hand is never yanked.
  useEffect(() => {
    if (!redirectArmed || !isTerminalMode || appState !== 'main') return;
    const timer = setTimeout(() => {
      setRedirectArmed(false);
      setIsTerminalMode(false);
    }, IDLE_REDIRECT_MS);
    return () => clearTimeout(timer);
  }, [redirectArmed, isTerminalMode, appState, activityTick]);

  useEffect(() => {
    if (commandHistory.length > 0 && terminalRef.current) {
      setTimeout(() => {
        if (terminalRef.current) {
          terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [commandHistory, currentDirectory]);

  useEffect(() => {
    if (showInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [appState, showInput]);

  const showBios = appState === 'bios';
  const showNotesApp = appState === 'notes';
  const showPlanner = appState === 'planner';
  const showDeltaForce = appState === 'delta-force';
  const showFitness = appState === 'fitness';

  const TopNavBar = ({ activeSection, onNavClick }) => {
    const sections = ['about', 'experience', 'projects', 'contact'];
    return (
      <nav className="top-nav-bar">
        {sections.map(section => (
          <button
            key={section}
            className={`nav-button ${activeSection === section ? 'active' : ''}`}
            onClick={() => onNavClick(section)}
          >
            {section.charAt(0).toUpperCase() + section.slice(1)}
          </button>
        ))}
      </nav>
    );
  };

  const WebsiteView = ({ isDarkMode, isTerminalMode, setIsDarkMode, setIsTerminalMode, directories }) => {
    const [activeSection, setActiveSection] = useState('about');
    const [activeProject, setActiveProject] = useState(null);

    const sections = {
      about: directories['~/about'],
      experience: directories['~/experience'],
      projects: directories['~/projects'],
      contact: directories['~/contact']
    };

    const handleNavClick = (section) => {
      setActiveSection(section);
      setActiveProject(null); // Reset project view when changing main section
    };

    return (
      <div className={`website-container ${isDarkMode ? 'dark' : 'light'}`}>
        <div className="main-layout">
          <div className="left-column">
            <header className="main-title">
              <h1>Rohit Rao</h1>
            </header>
            <TopNavBar activeSection={activeSection} onNavClick={handleNavClick} />
            <main className="website-content">
              {activeProject ? (
                <div className="project-detail">
                  <div className="section-text">
                    {directories[`~/projects/${activeProject}`].content.split('\n').map((line, index) => (
                      <div key={index} className="content-line">{line}</div>
                    ))}
                  </div>
                  <button className="back-button" onClick={() => setActiveProject(null)}>← Back to Projects</button>
                </div>
              ) : (
                <>
                  <div className="section-text">
                    {sections[activeSection].content.split('\n').map((line, index) => (
                      <div key={index} className="content-line">{line}</div>
                    ))}
                  </div>
                  {activeSection === 'projects' && directories['~/projects'].children && (
                    <div className="projects-list">
                      {directories['~/projects'].children.map(projectKey => {
                        const project = directories[`~/projects/${projectKey}`];
                        return (
                          <button className="project-item-button" key={projectKey} onClick={() => setActiveProject(projectKey)}>
                            » {project.title}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </main>
          </div>
          <div className="right-column">
            <div className="sidebar">
              <div className="sidebar-section">
                <ul className="sidebar-list">
                  {Object.keys(sections).map(section => (
                      <li key={section} className={`sidebar-item ${activeSection === section ? 'active' : ''}`} onClick={() => handleNavClick(section)}>
                          {section.charAt(0).toUpperCase() + section.slice(1)}
                      </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`App ${isDarkMode ? 'dark' : 'light'} ${isTerminalMode ? 'terminal-view' : 'website-view'}`}>
      {showBios ? (
        <BiosLoginView onLoginSuccess={handleLoginSuccess} onBack={() => setAppState('main')} />
      ) : showDeltaForce ? (
        <DeltaForce onExit={() => setAppState('main')} />
      ) : showPlanner ? (
        authChecked && user ? (
          <div className={`planner-root ${isDarkMode ? 'dark' : 'light'}`}><PlannerView setAppState={setAppState} user={user} isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} setInitialNoteId={setInitialNoteId} /></div>
        ) : (
          <div className="auth-required">
            <h2>Authentication Required</h2>
            <p>Please use the "ro init" command to access the planner.</p>
            <button onClick={() => setAppState('main')}>Back to Main</button>
          </div>
        )
      ) : showFitness ? (
        authChecked && user ? (
          <Fitness user={user} setAppState={setAppState} isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />
        ) : (
          <div className="auth-required">
            <h2>Authentication Required</h2>
            <p>Please use the "ro init" command to access the fitness app.</p>
            <button onClick={() => setAppState('main')}>Back to Main</button>
          </div>
        )
      ) : showNotesApp ? (
        authChecked && user ? (
          <div className={`notes-app-container ${isDarkMode ? 'dark' : 'light'}`}><NotesAppView setAppState={setAppState} user={user} isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} initialNoteId={initialNoteId} setInitialNoteId={setInitialNoteId} /></div>
        ) : (
          <div className="auth-required">
            <h2>Authentication Required</h2>
            <p>Please use the "ro init" command to access the notes app.</p>
            <button onClick={() => setAppState('main')}>Back to Main</button>
          </div>
        )
      ) : isTerminalMode ? (
        <div className="terminal" onClick={() => inputRef.current?.focus()}>
          <div className="terminal-content" ref={terminalRef}>
            {commandHistory.map((entry, index) => (
              <div key={index} className="command-entry">
                {entry.type === 'intro' || entry.type === 'error' || entry.type === 'hint' ? (
                  <div className={`command-output ${entry.type === 'error' ? 'error-output' : ''}${entry.type === 'hint' ? 'hint-output' : ''}`}>
                    <div className="output-line">{entry.output}</div>
                  </div>
                ) : (
                  <>
                    <div className="command-line">
                      <span className="prompt">
                        {entry.prompt ? entry.prompt : `rohit@rao:${entry.directory || '~'}$`}
                      </span>
                      <span className="command">{entry.command}</span>
                    </div>
                    {entry.output && (
                      <div className="command-output">
                        {entry.output.split('\n').map((line, lineIndex) => (
                          <div key={lineIndex} className="output-line">{line}</div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
            
            {showInput && (
              <div className="command-line current">
                <span className="prompt">{`rohit@rao:${currentDirectory}$`}</span>
                <span className="input-wrap">
                  <input
                    ref={inputRef}
                    type="text"
                    value={currentCommand}
                    onChange={(e) => { setCurrentCommand(e.target.value); setActivityTick(t => t + 1); }}
                    onKeyDown={handleKeyPress}
                    className="command-input"
                    autoComplete="off"
                    disabled={appState === 'shutdown'}
                  />
                  {showGhost && (
                    <span className="ghost-command" key={ghostIndex} aria-hidden="true">
                      {DEMO_COMMANDS[ghostIndex]}
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <WebsiteView 
          isDarkMode={isDarkMode}
          isTerminalMode={isTerminalMode}
          setIsDarkMode={setIsDarkMode}
          setIsTerminalMode={setIsTerminalMode}
          directories={directories}
        />
      )}
      {!showPlanner && !showNotesApp && !showBios && !isTerminalMode && (
        <div className="toggle-buttons-container">
          <button 
            className={`theme-toggle ${isDarkMode ? 'dark' : 'light'}`}
            onClick={() => setIsDarkMode(!isDarkMode)}
            title="Toggle theme"
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>
          <button 
            className={`mode-toggle ${isTerminalMode ? 'terminal' : 'website'}`}
            onClick={() => setIsTerminalMode(!isTerminalMode)}
            title="Toggle interface mode"
          >
            {isTerminalMode ? '🌐' : '💻'}
          </button>
        </div>
      )}
    </div>
  );
}

// The only account firestore.rules / storage.rules let through. Keep in sync.
const OWNER_EMAIL = 'raoisright@gmail.com';

const BiosLoginView = ({ onLoginSuccess, onBack }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  // ESC key handler for BIOS login
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && !isAuthenticating) {
        // Clear password and error on ESC
        setPassword('');
        setError('');
      }
    };

    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [isAuthenticating]);





  const handleLogin = async (e) => {
    e.preventDefault();
    if (password === '' || isAuthenticating) return;
    setError('');
    setIsAuthenticating(true);

    // In a production environment, the URL would be the deployed function URL.
    // For local development, we use the emulator URL.
    const url = window.location.hostname === 'localhost'
      ? 'http://127.0.0.1:5001/rohit-rao-net/us-central1/verifyPassword'
      : 'https://us-central1-rohit-rao-net.cloudfunctions.net/verifyPassword';

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (response.ok) {
        // Password is correct, now handle authentication
        const authInstance = getAuthInstance();
        if (!authInstance) {
          setError('Authentication not available.');
          setIsAuthenticating(false);
          return;
        }

        // Firebase remembers the last account that signed in to this site, independent
        // of which Google account the browser is using. Keep that session only if it's
        // the owner; otherwise drop it so the account chooser appears.
        await authInstance.authStateReady();
        if (authInstance.currentUser?.email === OWNER_EMAIL) {
          onLoginSuccess();
          return;
        }
        if (authInstance.currentUser) {
          console.log('Signing out non-owner session:', authInstance.currentUser.email);
          await signOut(authInstance);
        }

        // Always show the account chooser; a silent sign-in picks the browser's
        // default Google account, which may not be the owner's.
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: 'select_account' });

        try {
          const { user: signedIn } = await signInWithPopup(authInstance, provider);
          if (signedIn.email !== OWNER_EMAIL) {
            await signOut(authInstance);
            setError(`${signedIn.email} doesn't have access. Sign in as the site owner.`);
            setPassword('');
            return;
          }
          onLoginSuccess();
        } catch (popupError) {
          console.error('Popup sign-in failed:', popupError);

          // Handle specific error cases
          if (popupError.code === 'auth/popup-closed-by-user') {
            setError('Sign-in was cancelled. Please try again.');
          } else if (popupError.code === 'auth/popup-blocked') {
            setError('Popup was blocked. Please allow popups and try again.');
          } else if (popupError.code === 'auth/cancelled-popup-request') {
            setError('Sign-in was cancelled. Please try again.');
          } else {
            setError(`Sign-in failed: ${popupError.message}`);
          }
          setPassword('');
        }
      } else {
        const data = await response.json();
        setError(data.error || 'Incorrect password');
        setPassword('');
      }
    } catch (err) {
      console.error("Error verifying password:", err);
      setError('An error occurred. Please try again.');
      setPassword('');
    } finally {
      setIsAuthenticating(false);
    }
  };

  return (
    <div className="bios-container">
      <form onSubmit={handleLogin} className="bios-login">
        <label htmlFor="password">SYSTEM ADMIN</label>
        <input
          type="password"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoFocus
          disabled={isAuthenticating}
        />
        <button type="submit" disabled={isAuthenticating}>
          {isAuthenticating ? 'Authenticating...' : 'Login'}
        </button>
        {error && <p className="login-error">{error}</p>}
        <button type="button" className="back-to-terminal" onClick={onBack} disabled={isAuthenticating}>
          Back to Terminal
        </button>
      </form>
    </div>
  );
};

const FormattingPopup = ({ editor, isOpen, onClose }) => {
  const [fontSize, setFontSize] = useState(18);
  const fontSizeInputRef = useRef(null);

  // Effect to update the font size input when editor selection changes
  useEffect(() => {
    if (!editor || !isOpen) {
      return;
    }

    const updateFontSize = () => {
      // If user is typing in the input, don't fight with them.
      if (document.activeElement === fontSizeInputRef.current) {
        return;
      }
      const size = editor.getAttributes('textStyle').fontSize;
      setFontSize(size ? parseInt(size, 10) : 18);
    };

    editor.on('transaction', updateFontSize);
    editor.on('selectionUpdate', updateFontSize);

    return () => {
      editor.off('transaction', updateFontSize);
      editor.off('selectionUpdate', updateFontSize);
    };
  }, [editor, isOpen]);

  const handleFontSizeChange = (e) => {
    const newSize = e.target.value;
    setFontSize(newSize); // Update the input field view immediately

    if (newSize === '') {
      editor.chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run();
    } else {
      const parsedSize = parseInt(newSize, 10);
      if (parsedSize > 0) {
        editor.chain().setMark('textStyle', { fontSize: `${parsedSize}px` }).run();
      }
    }
  };

  // ESC key handler
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleEscKey);
      return () => window.removeEventListener('keydown', handleEscKey);
    }
  }, [isOpen, onClose]);

  if (!isOpen || !editor) {
    return null;
  }

  return (
    <div className="formatting-popup-overlay">
      <div className="formatting-popup-modal">
        <div className="formatting-popup-header">
          <h3>Text Formatting</h3>
          <button className="formatting-popup-close" onClick={onClose}>×</button>
        </div>
        
        <div className="formatting-popup-content">
          <div className="formatting-section">
            <h4>History</h4>
            <div className="formatting-buttons">
              <button 
                onClick={() => editor.chain().undo().run()} 
                disabled={!editor.can().undo()}
                className="formatting-btn"
              >
                Undo
              </button>
              <button 
                onClick={() => editor.chain().redo().run()} 
                disabled={!editor.can().redo()}
                className="formatting-btn"
              >
                Redo
              </button>
            </div>
          </div>
          
          <div className="formatting-section">
            <h4>Text Style</h4>
            <div className="formatting-buttons">
              <button
                onClick={() => editor.chain().toggleBold().run()}
                disabled={!editor.can().chain().toggleBold().run()}
                className={`formatting-btn ${editor.isActive('bold') ? 'active' : ''}`}
              >
                <strong>B</strong>
              </button>
              <button
                onClick={() => editor.chain().toggleItalic().run()}
                disabled={!editor.can().chain().toggleItalic().run()}
                className={`formatting-btn ${editor.isActive('italic') ? 'active' : ''}`}
              >
                <em>I</em>
              </button>
            </div>
          </div>
          
          <div className="formatting-section">
            <h4>Formatting</h4>
            <div className="formatting-buttons">
              <button
                onClick={() => editor.chain().toggleBulletList().run()}
                className={`formatting-btn ${editor.isActive('bulletList') ? 'active' : ''}`}
              >
                •
              </button>
              <button
                onClick={() => editor.chain().toggleBlockquote().run()}
                className={`formatting-btn ${editor.isActive('blockquote') ? 'active' : ''}`}
              >
                "
              </button>
            </div>
          </div>
          
          <div className="formatting-section">
            <h4>Table</h4>
            <div className="formatting-buttons">
              <button
                onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                className="formatting-btn"
                title="Insert 3x3 table"
              >
                ⊞
              </button>
              <button
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                disabled={!editor.can().addColumnAfter()}
                className="formatting-btn"
                title="Add column"
              >
                +Col
              </button>
              <button
                onClick={() => editor.chain().focus().addRowAfter().run()}
                disabled={!editor.can().addRowAfter()}
                className="formatting-btn"
                title="Add row"
              >
                +Row
              </button>
              <button
                onClick={() => editor.chain().focus().deleteColumn().run()}
                disabled={!editor.can().deleteColumn()}
                className="formatting-btn"
                title="Delete column"
              >
                −Col
              </button>
              <button
                onClick={() => editor.chain().focus().deleteRow().run()}
                disabled={!editor.can().deleteRow()}
                className="formatting-btn"
                title="Delete row"
              >
                −Row
              </button>
              <button
                onClick={() => editor.chain().focus().deleteTable().run()}
                disabled={!editor.can().deleteTable()}
                className="formatting-btn"
                title="Delete table"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="formatting-section">
            <h4>Font Size</h4>
            <div className="font-size-control">
              <input
                ref={fontSizeInputRef}
                type="number"
                className="font-size-input"
                value={fontSize}
                onChange={handleFontSizeChange}
                min="8"
                max="72"
              />
              <span className="font-size-unit">px</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PlannerView = ({ setAppState, user, isDarkMode, setIsDarkMode, setInitialNoteId }) => {
  const START_HOUR = 5; // 5 AM
  const END_HOUR = 29; // 4 AM next day (so 5 AM to 4 AM inclusive)

  const [tasks, setTasks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showNoteSelector, setShowNoteSelector] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskStart, setNewTaskStart] = useState('');
  const [newTaskEnd, setNewTaskEnd] = useState('');
  const [newTaskCompleted, setNewTaskCompleted] = useState(false);
  const [newTaskCore, setNewTaskCore] = useState(false);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [addTaskError, setAddTaskError] = useState('');
  const scrollContainerRef = useRef(null);
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [dragOverHour, setDragOverHour] = useState(null);
  const [containerHeight, setContainerHeight] = useState(540); // default fallback
  const canvasOuterRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editTaskName, setEditTaskName] = useState('');
  const [editTaskId, setEditTaskId] = useState(null); // null means add, otherwise edit
  // In PlannerView, add state for birthday modal and birthday list
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);
  const [birthdays, setBirthdays] = useState([]);
  const [birthdayName, setBirthdayName] = useState('');
  const [birthdayDate, setBirthdayDate] = useState('');
  const [birthdaySearch, setBirthdaySearch] = useState('');
  const [showAddBirthdayForm, setShowAddBirthdayForm] = useState(false);
  // Add schedule type state for weekday/weekend toggle
  const [scheduleType, setScheduleType] = useState(() => {
    // Automatically set schedule type based on current day
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    return (dayOfWeek === 0 || dayOfWeek === 6) ? 'weekend' : 'weekday';
  }); // 'weekday' or 'weekend'
  
  // Add live counter state
  const [coreTaskProgress, setCoreTaskProgress] = useState({ completed: 0, total: 0 });
  const [timeUntilReset, setTimeUntilReset] = useState('');
  const [streakCount, setStreakCount] = useState(0);

  // Add state for streak history modal
  const [showStreakModal, setShowStreakModal] = useState(false);
  const [streakHistory, setStreakHistory] = useState([]);
  const [totalStreakDays, setTotalStreakDays] = useState(0);

  // Exit confirmation popup state
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);

  // ESC key handlers for all modals
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        // Close modals in order of priority
        if (showAddTaskModal) {
          setShowAddTaskModal(false);
          setEditTaskId(null);
          setAddTaskError('');
        } else if (showNoteSelector) {
          setShowNoteSelector(false);
          setSelectedTask(null);
        } else if (showBirthdayModal) {
          setShowBirthdayModal(false);
          setBirthdaySearch('');
          setShowAddBirthdayForm(false);
        } else if (showEditTaskModal) {
          setShowEditTaskModal(false);
          setEditTaskId(null);
          setEditTaskName('');
        } else if (showStreakModal) {
          setShowStreakModal(false);
        } else if (showExitConfirmation) {
          setShowExitConfirmation(false);
        } else if (selectedTask) {
          setSelectedTask(null);
        }
      }
    };

    // Only add listener if any modal is open
    if (showAddTaskModal || showNoteSelector || showBirthdayModal || showEditTaskModal || showStreakModal || showExitConfirmation || selectedTask) {
      window.addEventListener('keydown', handleEscKey);
      return () => window.removeEventListener('keydown', handleEscKey);
    }
  }, [showAddTaskModal, showNoteSelector, showBirthdayModal, showEditTaskModal, showStreakModal, showExitConfirmation, selectedTask]);

  // Handle schedule type change
  const handleScheduleTypeChange = (newType) => {
    console.log(`Switching from ${scheduleType} to ${newType}`);
    setScheduleType(newType);
    setSelectedTask(null); // Clear selected task when switching schedules
    
    // Immediately load tasks for the new schedule type
    const loadTasksForSchedule = async (scheduleTypeToLoad) => {
      if (!user) return;
      try {
        const dbInstance = getDbInstance();
        if (!dbInstance) return;
        const tasksCollectionRef = collection(dbInstance, "tasks");
        const data = await getDocs(tasksCollectionRef);
        const sortedTasks = data.docs
          .map((doc) => ({ ...doc.data(), id: doc.id }))
          .map(task => ({
            ...task,
            scheduleType: task.scheduleType || 'weekday' // Default to weekday for existing tasks
          }))
          .filter(task => task.scheduleType === scheduleTypeToLoad) // Filter by the new schedule type
          .sort((a, b) => a.startTime - b.startTime);
        
        console.log(`Loaded ${sortedTasks.length} tasks for ${scheduleTypeToLoad} schedule`);
        setTasks(sortedTasks);
      } catch (error) {
        console.log("Could not fetch tasks:", error);
      }
    };
    
    loadTasksForSchedule(newType);
  };

  // Exit confirmation handlers
  const handleExitClick = () => {
    setShowExitConfirmation(true);
  };

  const handleExitConfirm = () => {
    setShowExitConfirmation(false);
    setAppState('main');
  };

  const handleExitCancel = () => {
    setShowExitConfirmation(false);
  };

  // Dynamically set HOUR_HEIGHT and WINDOW_HEIGHT based on container height
  const VISIBLE_HOURS = 6;
  const HOUR_HEIGHT = Math.floor(containerHeight / VISIBLE_HOURS);

  useEffect(() => {
    const updateHeight = () => {
      if (canvasOuterRef.current) {
        setContainerHeight(canvasOuterRef.current.clientHeight);
      }
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);


  // Get current time and update every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 5000); // every 5 seconds
    return () => clearInterval(timer);
  }, []);



  // Load notes from Firestore
  const loadNotes = useCallback(async () => {
    if (!user) return;
    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      const notesCollectionRef = collection(dbInstance, "notes");
      const data = await getDocs(notesCollectionRef);
      const sortedNotes = data.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setNotes(sortedNotes);
    } catch (error) {
      console.log("Could not fetch notes:", error);
    }
  }, [user]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);





  // Load tasks from Firestore - modified to filter by schedule type and cleanup old tasks
  const loadTasks = useCallback(async () => {
    if (!user) return;
    try {
      // First cleanup old non-core tasks
      const cleanupOldTasks = async () => {
    if (!user) return;
    try {
          const dbInstance = getDbInstance();
          if (!dbInstance) return;
          
          const now = new Date();
          const today5AM = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 5, 0, 0, 0);
          
          // If it's before 5 AM, use yesterday's 5 AM as cutoff
          const cutoffTime = now < today5AM ? 
            new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 5, 0, 0, 0) :
            today5AM;
          
          const tasksCollectionRef = collection(dbInstance, "tasks");
          const data = await getDocs(tasksCollectionRef);
          
          const tasksToDelete = data.docs
            .map((doc) => ({ ...doc.data(), id: doc.id }))
            .filter(task => 
              !task.core && // Only delete non-core tasks
              task.scheduleType === scheduleType && // Only delete tasks for current schedule
              task.endTime < cutoffTime.getTime() // Only delete tasks that ended before cutoff
            );
          
          if (tasksToDelete.length > 0) {
            console.log(`Cleaning up ${tasksToDelete.length} old non-core tasks`);
            const deletePromises = tasksToDelete.map(task => 
              deleteDoc(doc(dbInstance, "tasks", task.id))
            );
            await Promise.all(deletePromises);
            console.log(`Successfully deleted ${tasksToDelete.length} old tasks`);
          }
        } catch (error) {
          console.log("Could not cleanup old tasks:", error);
        }
      };
      
      await cleanupOldTasks();
      
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      const tasksCollectionRef = collection(dbInstance, "tasks");
      const data = await getDocs(tasksCollectionRef);
      const sortedTasks = data.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .map(task => ({
          ...task,
          scheduleType: task.scheduleType || 'weekday' // Default to weekday for existing tasks
        }))
        .filter(task => task.scheduleType === scheduleType) // Filter by schedule type
        .sort((a, b) => a.startTime - b.startTime);
      
      console.log(`Loaded ${sortedTasks.length} tasks for ${scheduleType} schedule`);
      setTasks(sortedTasks);
    } catch (error) {
      console.log("Could not fetch tasks:", error);
    }
  }, [user, scheduleType]); // Removed cleanupOldTasks dependency



  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Periodic cleanup of old non-core tasks (every hour)
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      loadTasks(); // This will also cleanup old tasks
    }, 60 * 60 * 1000); // Run every hour

    return () => clearInterval(cleanupInterval);
  }, [loadTasks]);

  // Calculate core task progress
  const calculateCoreTaskProgress = useCallback(() => {
    const coreTasks = tasks.filter(task => task.core && task.scheduleType === scheduleType);
    const completed = coreTasks.filter(task => task.completed).length;
    const total = coreTasks.length;
    setCoreTaskProgress({ completed, total });
  }, [tasks, scheduleType]);

  // Get user's timezone
  const getUserTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      // Fallback to a default timezone if detection fails
      return 'America/Los_Angeles';
    }
  };

  // Calculate time until reset (5 AM)
  const calculateTimeUntilReset = useCallback(() => {
    const now = new Date();
    const userTimezone = getUserTimezone();
    
    // Create 5 AM today in user's timezone
    const today5AM = new Date(now.toLocaleString("en-US", {timeZone: userTimezone}));
    today5AM.setHours(5, 0, 0, 0);
    
    // If it's before 5 AM today, countdown to today's 5 AM
    // If it's after 5 AM today, countdown to tomorrow's 5 AM
    let nextResetTime;
    if (now < today5AM) {
      nextResetTime = today5AM;
    } else {
      const tomorrow5AM = new Date(today5AM);
      tomorrow5AM.setDate(tomorrow5AM.getDate() + 1);
      nextResetTime = tomorrow5AM;
    }
    
    const timeDiff = nextResetTime.getTime() - now.getTime();
    
    if (timeDiff <= 0) {
      setTimeUntilReset('0 hours');
      return;
    }
    
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      setTimeUntilReset(`${hours} hour${hours > 1 ? 's' : ''} ${minutes > 0 ? `${minutes} minute${minutes > 1 ? 's' : ''}` : ''}`);
    } else {
      setTimeUntilReset(`${minutes} minute${minutes > 1 ? 's' : ''}`);
    }
  }, []);



  // Update core task progress when tasks change
  useEffect(() => {
    calculateCoreTaskProgress();
  }, [tasks, scheduleType, calculateCoreTaskProgress]);

  // Update time until reset every minute
  useEffect(() => {
    calculateTimeUntilReset();
    const timeInterval = setInterval(() => {
      calculateTimeUntilReset();
    }, 60 * 1000); // Update every minute

    return () => clearInterval(timeInterval);
  }, [calculateTimeUntilReset]);

  const deleteTask = React.useCallback(async (taskId) => {
    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      const taskDoc = doc(dbInstance, "tasks", taskId);
      await deleteDoc(taskDoc);
      await loadTasks();
    } catch (error) {
      console.log("Could not delete task:", error);
    }
  }, [loadTasks]);

  const handleEditTask = (task, action) => {
    setSelectedTask(task);
    switch (action) {
      case 'linkNote':
        setShowNoteSelector(true);
        break;
      case 'rename':
        setNewTaskTitle(task.title);
        setNewTaskStart(formatTimeForInput(new Date(task.startTime)));
        setNewTaskEnd(formatTimeForInput(new Date(task.endTime)));
        setNewTaskCompleted(task.completed || false);
        setNewTaskCore(task.core || false);
        setEditTaskId(task.id);
        setShowAddTaskModal(true);
        setAddTaskError('');
        break;
      case 'delete':
        deleteTask(task.id);
        break;
      default:
        break;
    }
  };

  const handleNoteSelect = (noteId) => {
    if (selectedTask) {
      updateTask(selectedTask.id, { noteId });
    }
    setShowNoteSelector(false);
    setSelectedTask(null); // Close the task edit menu after linking a note
  };

  // Instead, define currentTask directly:
    const now = currentTime.getTime();
  const currentTask = tasks.find(task => now >= task.startTime && now <= task.endTime);

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Helper: get all hour slots from 5 AM to 4 AM next day
  const getHourSlots = () => {
    const today = new Date();
    const slots = [];
    // Generate 24 hours: 5 AM today to 4 AM tomorrow (5 to 28 inclusive = 24 hours)
    for (let h = 5; h <= 28; h++) {
      let dayOffset = h >= 24 ? 1 : 0;
      let hourVal = h % 24;
      const hour = new Date(today.getFullYear(), today.getMonth(), today.getDate() + dayOffset, hourVal, 0, 0, 0);
      slots.push(hour);
    }
    return slots;
  };

  // Helper: find scroll index for current time
  const getCurrentHourIndex = () => {
      const now = new Date();
    let hour = now.getHours();
    if (hour < START_HOUR) hour = START_HOUR;
    if (hour >= END_HOUR) hour = END_HOUR - 1;
    return hour - START_HOUR;
  };

  // Scroll to current hour/task on mount
  useEffect(() => {
    if (scrollContainerRef.current) {
      const hourIndex = getCurrentHourIndex();
      const hourHeight = 60; // px, matches .time-mark height
      scrollContainerRef.current.scrollTop = Math.max(0, hourIndex * hourHeight - 2 * hourHeight);
    }
  }, [tasks.length]);

  // Map tasks to hour slots for display
  const hourSlots = getHourSlots();

  // (getTaskForHour removed, not used)

  // Helper: format time for dropdown
  // const formatTimeInput = (date) => {
  //   return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  // };

  // Helper: parse time input (HH:mm) to Date
  // const parseTimeInput = (value) => {
  //   const [h, m] = value.split(':').map(Number);
  //   const d = new Date();
  //   d.setHours(h, m, 0, 0);
  //   return d;
  // };

  // Add/Edit Task Modal logic
  const handleAddTaskClick = () => {
    setShowAddTaskModal(true);
    setNewTaskTitle('');
    setNewTaskStart('');
    setNewTaskEnd('');
    setNewTaskCompleted(false);
    setNewTaskCore(false);
    setAddTaskError('');
    setEditTaskId(null);
  };

  const handleAddTaskSubmit = async (e) => {
    e.preventDefault();
    if (!newTaskTitle.trim() || !newTaskStart || !newTaskEnd) {
      setAddTaskError('Please fill in all fields');
      return;
    }

    setIsAddingTask(true);
    setAddTaskError('');

    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;

      const [startHour, startMinute] = newTaskStart.split(':').map(Number);
      const [endHour, endMinute] = newTaskEnd.split(':').map(Number);

      const today = new Date();
      // Handle times that span across midnight
      let startTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), startHour, startMinute, 0, 0);
      let endTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), endHour, endMinute, 0, 0);
      
      // If end time is before start time, it means it's the next day
      if (endTime <= startTime) {
        endTime = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1, endHour, endMinute, 0, 0);
      }

      // Validate that the task duration is reasonable (not more than 24 hours)
      const durationMs = endTime.getTime() - startTime.getTime();
      const durationHours = durationMs / (60 * 60 * 1000);
      if (durationHours > 24) {
        setAddTaskError('Task duration cannot exceed 24 hours');
        setIsAddingTask(false);
        return;
      }

      // Check for overlapping times with existing tasks
      const overlappingTask = tasks.find(task => {
        // Skip the task being edited (if editing)
        if (editTaskId && task.id === editTaskId) {
          return false;
        }
        
        // Check if the new task overlaps with this existing task
        const taskStart = new Date(task.startTime);
        const taskEnd = new Date(task.endTime);
        
        // Two time ranges overlap if:
        // 1. New task starts before existing task ends AND
        // 2. New task ends after existing task starts
        return startTime < taskEnd && endTime > taskStart;
      });

      if (overlappingTask) {
        const overlappingStart = new Date(overlappingTask.startTime).toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
        const overlappingEnd = new Date(overlappingTask.endTime).toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
        setAddTaskError(`Time slot overlaps with existing task "${overlappingTask.title}" (${overlappingStart} - ${overlappingEnd})`);
        setIsAddingTask(false);
        return;
      }

      const tasksCollectionRef = collection(dbInstance, "tasks");
      const newTask = {
        title: newTaskTitle.trim(),
        startTime: startTime.getTime(),
        endTime: endTime.getTime(),
        completed: newTaskCompleted,
        core: newTaskCore,
        scheduleType: scheduleType, // Add schedule type to new tasks
        userId: user.uid,
        createdAt: Date.now()
      };

      if (editTaskId) {
        // Update existing task
        const taskDoc = doc(dbInstance, "tasks", editTaskId);
        await updateDoc(taskDoc, {
          title: newTask.title,
          startTime: newTask.startTime,
          endTime: newTask.endTime,
          completed: newTask.completed,
          core: newTask.core,
          scheduleType: scheduleType // Update schedule type for edited tasks
        });
      } else {
        // Add new task
      await addDoc(tasksCollectionRef, newTask);
      }

      setNewTaskTitle('');
      setNewTaskStart('');
      setNewTaskEnd('');
      setNewTaskCompleted(false);
      setNewTaskCore(false);
      setShowAddTaskModal(false);
      setEditTaskId(null);
      await loadTasks();
    } catch (error) {
      console.log("Could not add task:", error);
      setAddTaskError('Failed to add task. Please try again.');
    } finally {
      setIsAddingTask(false);
    }
  };

  // Edit Task logic: check for overlaps before updating
  const updateTask = async (taskId, updates) => {
    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;
      
      let newStart = updates.startTime !== undefined ? updates.startTime : task.startTime;
      let newEnd = updates.endTime !== undefined ? updates.endTime : task.endTime;
      
      // Check for overlapping times with other tasks (excluding the current task being edited)
      const overlappingTask = tasks.find(otherTask => {
        if (otherTask.id === taskId) {
          return false; // Skip the task being edited
        }
        
        const otherStart = new Date(otherTask.startTime);
        const otherEnd = new Date(otherTask.endTime);
        
        // Two time ranges overlap if:
        // 1. New task starts before existing task ends AND
        // 2. New task ends after existing task starts
        return newStart < otherEnd && newEnd > otherStart;
      });

      if (overlappingTask) {
        const overlappingStart = new Date(overlappingTask.startTime).toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
        const overlappingEnd = new Date(overlappingTask.endTime).toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
        console.log(`Cannot update task: overlaps with "${overlappingTask.title}" (${overlappingStart} - ${overlappingEnd})`);
        return; // Don't update if there's an overlap
      }
      
      await updateDoc(doc(dbInstance, "tasks", taskId), { ...updates, startTime: newStart, endTime: newEnd });
      await loadTasks();
    } catch (error) {
      console.log("Could not update task:", error);
    }
  };

  const handleEditTaskModalSubmit = async (e) => {
    e.preventDefault();
    if (editTaskId && editTaskName.trim()) {
      await updateTask(editTaskId, { title: editTaskName.trim() });
      setShowEditTaskModal(false);
      setEditTaskId(null);
      setEditTaskName('');
    }
  };

  // Drag and drop handlers
  // const handleDragStart = (taskId, e) => {
  //   setDraggedTaskId(taskId);
  //   e.dataTransfer && e.dataTransfer.setData('text/plain', taskId);
  //   e.dataTransfer && (e.dataTransfer.effectAllowed = 'move');
  // };
  // const handleDragEnd = () => {
  //   setDraggedTaskId(null);
  //   setDragOverHour(null);
  // };
  const handleDragOver = (hour, e) => {
    e.preventDefault();
    setDragOverHour(hour.getTime());
  };
  const handleDrop = async (hour, e) => {
    e.preventDefault();
    if (!draggedTaskId) return;
    const task = tasks.find(t => t.id === draggedTaskId);
    if (!task) return;
    
    // Snap to 30-min increments
    const newStart = new Date(hour);
    const duration = task.endTime - task.startTime;
    const snapped = new Date(Math.round(newStart.getTime() / (30*60000)) * 30*60000);
    const newEnd = new Date(snapped.getTime() + duration);
    
    // Check for overlapping times with other tasks (excluding the dragged task)
    const overlappingTask = tasks.find(otherTask => {
      if (otherTask.id === draggedTaskId) {
        return false; // Skip the task being dragged
      }
      
      const otherStart = new Date(otherTask.startTime);
      const otherEnd = new Date(otherTask.endTime);
      
      // Two time ranges overlap if:
      // 1. New task starts before existing task ends AND
      // 2. New task ends after existing task starts
      return snapped.getTime() < otherEnd && newEnd.getTime() > otherStart;
    });

    if (overlappingTask) {
      const overlappingStart = new Date(overlappingTask.startTime).toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      const overlappingEnd = new Date(overlappingTask.endTime).toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      alert(`Cannot move task: overlaps with "${overlappingTask.title}" (${overlappingStart} - ${overlappingEnd})`);
      setDraggedTaskId(null);
      setDragOverHour(null);
      return;
    }
    
    await updateTask(task.id, { startTime: snapped.getTime(), endTime: newEnd.getTime() });
    setDraggedTaskId(null);
    setDragOverHour(null);
  };

  // Context menu handlers
  // Remove any contextMenu and handleCloseContextMenu code and variables from PlannerView.

  // Track scroll position
  const handleScroll = (e) => {
    setScrollTop(e.target.scrollTop);
  };

  // Calculate the top visible hour index
  const topHourIndex = Math.floor(scrollTop / HOUR_HEIGHT);
  const visibleHourSlots = hourSlots.slice(topHourIndex, topHourIndex + VISIBLE_HOURS);
  const visibleWindowStart = visibleHourSlots[0];
  const visibleWindowEnd = visibleHourSlots[visibleHourSlots.length - 1];

  // Only render tasks that overlap with the visible window
  const visibleTasks = tasks.filter(task => {
    const taskStart = new Date(task.startTime);
    const taskEnd = new Date(task.endTime);
    return (
      taskEnd > visibleWindowStart && taskStart < new Date(visibleWindowEnd.getTime() + 60 * 60 * 1000)
    );
  });

  // Full scrollable height
  const fullScrollHeight = hourSlots.length * HOUR_HEIGHT;

  // In PlannerView, add keyboard navigation for task selection
  useEffect(() => {
    const handleTaskKeyDown = (e) => {
      if (!visibleTasks.length) return;
      if (!selectedTask) return;
      const idx = visibleTasks.findIndex(t => t.id === selectedTask.id);
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (idx < visibleTasks.length - 1) setSelectedTask(visibleTasks[idx + 1]);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (idx > 0) setSelectedTask(visibleTasks[idx - 1]);
      } else if (e.key === 'Delete') {
        // Only delete, not Backspace
        e.preventDefault();
        deleteTask(selectedTask.id);
        setSelectedTask(null);
      }
    };
    window.addEventListener('keydown', handleTaskKeyDown);
    return () => window.removeEventListener('keydown', handleTaskKeyDown);
  }, [selectedTask, visibleTasks, deleteTask]);

  // In the render, before mapping tasks:
  const sortedTasks = [...tasks].sort((a, b) => a.startTime - b.startTime);
  
  // Fix task positioning calculation
  const taskPositions = sortedTasks.map((task, idx) => {
    const taskStart = new Date(task.startTime);
    const taskEnd = new Date(task.endTime);
    
    // Calculate position based on time from 5 AM baseline
    const baselineTime = new Date(taskStart.getFullYear(), taskStart.getMonth(), taskStart.getDate(), 5, 0, 0, 0);
    
    // Handle tasks that span across the 4 AM boundary (5 AM to 4 AM next day)
    const nextDay459AM = new Date(taskStart.getFullYear(), taskStart.getMonth(), taskStart.getDate() + 1, 4, 59, 0, 0);
    const nextDay5AM = new Date(taskStart.getFullYear(), taskStart.getMonth(), taskStart.getDate() + 1, 5, 0, 0, 0);
    const segments = [];
    
    // Check if task spans across the 4:59 AM boundary (crosses from one day to the next)
    const spansBoundary = taskEnd > nextDay459AM && taskStart < nextDay459AM;
    
    if (spansBoundary) {
      // First segment: from task start to 4:59 AM
      const firstSegmentStart = taskStart;
      const firstSegmentEnd = new Date(taskStart.getFullYear(), taskStart.getMonth(), taskStart.getDate() + 1, 4, 59, 0, 0);
      
      if (firstSegmentStart < firstSegmentEnd) {
        // Calculate position for first segment (before 5 AM tasks go to bottom)
        let timeOffset;
        if (firstSegmentStart.getHours() < 5) {
          const hoursFrom5AM = (24 - 5) + firstSegmentStart.getHours();
          const minutesFrom5AM = firstSegmentStart.getMinutes();
          timeOffset = ((hoursFrom5AM * 60 + minutesFrom5AM) * 60 * 1000);
        } else {
          timeOffset = firstSegmentStart.getTime() - baselineTime.getTime();
        }
        const top = (timeOffset / (60 * 60 * 1000)) * HOUR_HEIGHT;
        const durationMs = firstSegmentEnd.getTime() - firstSegmentStart.getTime();
        const durationHours = durationMs / (60 * 60 * 1000);
        const height = durationHours * HOUR_HEIGHT;

        segments.push({
          id: `${task.id}-1`,
          top, 
          height, 
          displayStart: firstSegmentStart, 
          displayEnd: firstSegmentEnd,
          isFirstSegment: true
        });
      }
      
      // Second segment: from 5:00 AM to task end
      const secondSegmentStart = nextDay5AM;
      const secondSegmentEnd = taskEnd;
      
      const timeOffset = secondSegmentStart.getTime() - baselineTime.getTime();
      const top = (timeOffset / (60 * 60 * 1000)) * HOUR_HEIGHT;
      const durationMs = secondSegmentEnd.getTime() - secondSegmentStart.getTime();
      const durationHours = durationMs / (60 * 60 * 1000);
      const height = durationHours * HOUR_HEIGHT;
      
      segments.push({ 
        id: `${task.id}-2`, 
        top, 
        height, 
        displayStart: secondSegmentStart, 
        displayEnd: secondSegmentEnd,
        isFirstSegment: false
      });
    } else {
      // Single segment: task doesn't span boundary
      // Calculate position based on time from 5 AM baseline
      // For tasks before 5 AM, they come after 4:59 AM in the previous day's cycle
      let timeOffset;
      let displayEndTime = taskEnd;

      if (taskStart.getHours() < 5) {
        // Calculate position from 5 AM to 4:59 AM timeline
        // 2:00 AM should be positioned after 4:59 AM (at the bottom)
        const hoursFrom5AM = (24 - 5) + taskStart.getHours(); // Hours from 5 AM to 4:59 AM
        const minutesFrom5AM = taskStart.getMinutes();
        timeOffset = ((hoursFrom5AM * 60 + minutesFrom5AM) * 60 * 1000);

        // For tasks starting before 5 AM, clamp end time to 5 AM (same calendar day)
        // This is the end of the visible timeline for this planner day
        const sameDay5AM = new Date(taskStart.getFullYear(), taskStart.getMonth(), taskStart.getDate(), 5, 0, 0, 0);
        if (taskEnd > sameDay5AM) {
          displayEndTime = sameDay5AM;
        }
      } else {
        timeOffset = taskStart.getTime() - baselineTime.getTime();
      }
      const top = (timeOffset / (60 * 60 * 1000)) * HOUR_HEIGHT;
      const durationMs = displayEndTime.getTime() - taskStart.getTime();
      const durationHours = durationMs / (60 * 60 * 1000);
      const height = durationHours * HOUR_HEIGHT;

      segments.push({
        id: `${task.id}-1`,
        top,
        height,
        displayStart: taskStart,
        displayEnd: displayEndTime,
        isFirstSegment: true
      });
    }
    
    console.log(`Task: ${task.title}, Original: ${taskStart.toLocaleTimeString()}-${taskEnd.toLocaleTimeString()}, Segments:`, segments);
    console.log(`Task ${task.title} has ${segments.length} segments:`, segments.map(s => ({ top: s.top, height: s.height, display: `${s.displayStart.toLocaleTimeString()}-${s.displayEnd.toLocaleTimeString()}` })));
    
    return segments;
  });

  // In PlannerView, add a handler for Go To Linked Note
  const handleGoToLinkedNote = () => {
    if (!selectedTask) return;
    const linkedNote = notes.find(note => note.id === selectedTask.noteId);
    if (linkedNote) {
      setInitialNoteId(linkedNote.id);
      setAppState('notes');
    } else {
      alert('No linked Note');
    }
  };

  // Fetch birthdays and streak from Firestore on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const dbInstance = getDbInstance();
        if (!dbInstance) return;
        
        // Fetch birthdays
        const birthdaysCollectionRef = collection(dbInstance, "birthdays");
        const birthdaysData = await getDocs(birthdaysCollectionRef);
        const loadedBirthdays = birthdaysData.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        setBirthdays(loadedBirthdays);
        
        // Fetch streak data (without auto-incrementing)
        const streakData = await getDocs(collection(dbInstance, "streaks"));
        console.log("Streak data fetched:", streakData.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        if (!streakData.empty) {
          const streakDoc = streakData.docs[0];
          const streakDataObj = streakDoc.data();
          const currentStreak = streakDataObj.count || 0;
          const currentHistory = streakDataObj.history || [];
          const currentTotalDays = streakDataObj.totalDays || 0;
          
          // Ensure total days is at least equal to current streak
          const correctedTotalDays = Math.max(currentTotalDays, currentStreak);
          
          // Only update if the total days needs correction
          if (correctedTotalDays !== currentTotalDays) {
            await updateDoc(doc(dbInstance, "streaks", streakDoc.id), {
              totalDays: correctedTotalDays,
              lastUpdated: new Date()
            });
            setTotalStreakDays(correctedTotalDays);
          } else {
            setTotalStreakDays(currentTotalDays);
          }
          
          setStreakCount(currentStreak);
          setStreakHistory(currentHistory);
        } else {
          // No streak data exists yet
          setStreakCount(0);
          setTotalStreakDays(0);
          setStreakHistory([]);
        }
      } catch (error) {
        console.log("Could not fetch data:", error);
      }
    };
    fetchData();
  }, []);

  const handleAddBirthday = async (e) => {
    e.preventDefault();
    if (!birthdayName.trim() || !birthdayDate) return;
    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      const birthdaysCollectionRef = collection(dbInstance, "birthdays");
      const newBirthday = { name: birthdayName.trim(), date: birthdayDate };
      const docRef = await addDoc(birthdaysCollectionRef, newBirthday);
      setBirthdays(prev => [...prev, { ...newBirthday, id: docRef.id }]);
      setBirthdayName('');
      setBirthdayDate('');
    } catch (error) {
      // Optionally handle error
    }
  };

  // Birthday reminder logic (show all names with today's birthday)
  const today = new Date();
  const todayStr = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const birthdaysToday = birthdays.filter(b => {
    // b.date is yyyy-mm-dd, so check mm-dd
    const [, month, day] = b.date.split('-');
    return `${month}-${day}` === todayStr;
  });

  // In the planner-controls div, after the buttons, update:
  birthdaysToday.length > 0 &&
    <div style={{ marginTop: 12, color: '#27ae60', fontWeight: 600, fontSize: 18, textAlign: 'center' }}>
      {`Birthdays of Today: ${birthdaysToday.map(b => b.name).join(', ')}`}
    </div>

  // Add delete handler for birthdays
  const handleDeleteBirthday = async (birthday) => {
    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      if (!birthday.id) return;
      const birthdayDoc = doc(dbInstance, "birthdays", birthday.id);
      await deleteDoc(birthdayDoc);
      setBirthdays(prev => prev.filter(b => b.id !== birthday.id));
    } catch (error) {
      // Optionally handle error
    }
  };

  // Handle task container click
  const handleTaskClick = (e, task) => {
    // Check if the click target is the checkbox or any input element
    const target = e.target;
    if (target.type === 'checkbox' || 
        target.tagName === 'INPUT' || 
        target.closest('input[type="checkbox"]') || 
        target.closest('input') || 
        target.classList.contains('task-checkbox')) {
      // Don't open task options for checkbox clicks
      return;
    }
    setSelectedTask(task);
  };

  // Separate checkbox component that handles its own events
  const TaskCheckbox = ({ task, isCompleted }) => {
    const handleCheckboxChange = (e) => {
      e.stopPropagation();
      e.preventDefault();
      toggleCompleted(task.id, isCompleted);
    };

    const handleCheckboxClick = (e) => {
      e.stopPropagation();
      e.preventDefault();
    };

    const isDisabled = (() => {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const currentDayScheduleType = (dayOfWeek === 0 || dayOfWeek === 6) ? 'weekend' : 'weekday';
      return task.scheduleType !== currentDayScheduleType;
    })();

    return (
      <div 
        onClick={handleCheckboxClick}
        onMouseDown={handleCheckboxClick}
        style={{ display: 'inline-block' }}
      >
        <input
          type="checkbox"
          className="task-checkbox"
          checked={isCompleted}
          onChange={handleCheckboxChange}
          disabled={isDisabled}
          style={{
            width: '16px',
            height: '16px',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            margin: 0,
            opacity: isDisabled ? 0.5 : 1
          }}
        />
      </div>
    );
  };

  // Toggle task completion status
  const toggleCompleted = async (taskId, currentCompleted) => {
    try {
      // Find the task to check its schedule type
      const task = tasks.find(t => t.id === taskId);
      if (!task) {
        console.log("Task not found");
        return;
      }

      // Check if the task's schedule type matches the current actual day
      const today = new Date();
      const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      const currentDayScheduleType = (dayOfWeek === 0 || dayOfWeek === 6) ? 'weekend' : 'weekday';
      
      // Prevent completing tasks that don't match the current actual day
      if (task.scheduleType !== currentDayScheduleType) {
        console.log(`Cannot complete ${task.scheduleType} task on ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek]} (${currentDayScheduleType})`);
        return; // Simply return without doing anything
      }

      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      const taskDoc = doc(dbInstance, "tasks", taskId);
      await updateDoc(taskDoc, { completed: !currentCompleted });
      
      // Check if all core tasks are completed and update streak
      if (!currentCompleted) { // Only check when completing a task, not when uncompleting
        const coreTasks = tasks.filter(t => t.core && t.scheduleType === currentDayScheduleType);
        const completedCoreTasks = coreTasks.filter(t => t.completed || (t.id === taskId && !currentCompleted));
        
        if (completedCoreTasks.length === coreTasks.length && coreTasks.length > 0) {
          // All core tasks are completed, update streak
          const streaksCollectionRef = collection(dbInstance, "streaks");
          const streakData = await getDocs(streaksCollectionRef);
          
          if (!streakData.empty) {
            const streakDoc = streakData.docs[0];
            const streakDataObj = streakDoc.data();
            const currentStreak = streakDataObj.count || 0;
            const currentHistory = streakDataObj.history || [];
            const currentTotalDays = streakDataObj.totalDays || 0;
            
            // Add today's date to history if not already there
            const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format in local timezone
            const updatedHistory = currentHistory.includes(today) ? currentHistory : [...currentHistory, today];
            const newStreakCount = currentStreak + 1;
            const updatedTotalDays = Math.max(currentTotalDays + 1, newStreakCount, updatedHistory.length);
            
            await updateDoc(doc(dbInstance, "streaks", streakDoc.id), {
              count: newStreakCount,
              history: updatedHistory,
              totalDays: updatedTotalDays,
              lastUpdated: new Date()
            });
            
            setStreakCount(newStreakCount);
            setTotalStreakDays(updatedTotalDays);
            setStreakHistory(updatedHistory);
          } else {
            // Create new streak document
            const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format in local timezone
            await addDoc(collection(dbInstance, "streaks"), {
              count: 1,
              history: [today],
              totalDays: 1,
              lastUpdated: new Date()
            });
            setStreakCount(1);
            setTotalStreakDays(1);
            setStreakHistory([today]);
          }
        }
      } else {
        // Task is being uncompleted, check if we need to remove today from streak history
        const coreTasks = tasks.filter(t => t.core && t.scheduleType === currentDayScheduleType);
        const completedCoreTasks = coreTasks.filter(t => t.completed && t.id !== taskId);
        
        if (completedCoreTasks.length < coreTasks.length) {
          // Not all core tasks are completed anymore, remove today from streak history
          const streaksCollectionRef = collection(dbInstance, "streaks");
          const streakData = await getDocs(streaksCollectionRef);
          
          if (!streakData.empty) {
            const streakDoc = streakData.docs[0];
            const streakDataObj = streakDoc.data();
            const currentHistory = streakDataObj.history || [];
            const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format in local timezone
            
            // Remove today from history if it exists
            const updatedHistory = currentHistory.filter(date => date !== today);
            
            // Recalculate current streak (continuous days from the most recent date)
            let currentStreak = 0;
            if (updatedHistory.length > 0) {
              const sortedDates = updatedHistory.sort((a, b) => new Date(b) - new Date(a));
              const today = new Date();
              const todayStr = today.toLocaleDateString('en-CA'); // YYYY-MM-DD format in local timezone
              
              // Check if the most recent date is today or yesterday
              const mostRecentDate = sortedDates[0];
              
              if (mostRecentDate === todayStr) {
                // Most recent date is today, start counting consecutive days
                currentStreak = 1;
                
                // Check for consecutive days before this
                for (let i = 1; i < sortedDates.length; i++) {
                  const currentDate = new Date(sortedDates[i]);
                  const prevDate = new Date(sortedDates[i - 1]);
                  
                  // Calculate day difference
                  const dayDiff = Math.ceil((prevDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
                  
                  if (dayDiff === 1) {
                    currentStreak++;
                  } else {
                    break;
                  }
                }
              } else {
                // Check if most recent date is yesterday
                const yesterday = new Date();
                yesterday.setDate(yesterday.getDate() - 1);
                const yesterdayStr = yesterday.toLocaleDateString('en-CA');
                
                if (mostRecentDate === yesterdayStr) {
                  // Most recent date is yesterday, start counting consecutive days
                  currentStreak = 1;
                  
                  // Check for consecutive days before this
                  for (let i = 1; i < sortedDates.length; i++) {
                    const currentDate = new Date(sortedDates[i]);
                    const prevDate = new Date(sortedDates[i - 1]);
                    
                    // Calculate day difference
                    const dayDiff = Math.ceil((prevDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
                    
                    if (dayDiff === 1) {
                      currentStreak++;
                    } else {
                      break;
                    }
                  }
                }
              }
            }
            
            const updatedTotalDays = Math.max(updatedHistory.length, currentStreak);
            
            await updateDoc(doc(dbInstance, "streaks", streakDoc.id), {
              count: currentStreak,
              history: updatedHistory,
              totalDays: updatedTotalDays,
              lastUpdated: new Date()
            });
            
            setStreakCount(currentStreak);
            setTotalStreakDays(updatedTotalDays);
            setStreakHistory(updatedHistory);
          }
        }
      }
      
      await loadTasks(); // Reload tasks to get updated state
    } catch (error) {
      console.log("Could not update task completion:", error);
    }
  };

  // Add reset functionality for testing
  const handleTestReset = async () => {
    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      
      // Get all core tasks for the current schedule type
      const tasksCollectionRef = collection(dbInstance, "tasks");
      const data = await getDocs(tasksCollectionRef);
      const coreTasks = data.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .filter(task => task.core && task.scheduleType === scheduleType);
      
      if (coreTasks.length === 0) {
        alert('No core tasks found for the current schedule type.');
        return;
      }
      
      // Reset all core tasks to incomplete
      const resetPromises = coreTasks.map(task => 
        updateDoc(doc(dbInstance, "tasks", task.id), { completed: false })
      );
      await Promise.all(resetPromises);
      
      // Increment streak count and add to history
      const streaksCollectionRef = collection(dbInstance, "streaks");
      const streakData = await getDocs(streaksCollectionRef);
      
      if (!streakData.empty) {
        const streakDoc = streakData.docs[0];
        const currentStreak = streakDoc.data().count || 0;
        const currentHistory = streakDoc.data().history || [];
        const currentTotalDays = streakDoc.data().totalDays || 0;
        
        // Add today's date to history
        const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format in local timezone
        const updatedHistory = [...currentHistory, today];
        const newStreakCount = currentStreak + 1;
        const updatedTotalDays = Math.max(currentTotalDays + 1, newStreakCount, updatedHistory.length);
        
        await updateDoc(doc(dbInstance, "streaks", streakDoc.id), {
          count: newStreakCount,
          history: updatedHistory,
          totalDays: updatedTotalDays,
          lastUpdated: new Date()
        });
        setStreakCount(newStreakCount);
      } else {
        // Create new streak document
        const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD format in local timezone
        await addDoc(collection(dbInstance, "streaks"), {
          count: 1,
          history: [today],
          totalDays: 1,
          lastUpdated: new Date()
        });
        setStreakCount(1);
      }
      
      // Reload tasks to reflect changes
      await loadTasks();
      
      alert(`Test complete! ${coreTasks.length} core tasks reset to incomplete. Streak incremented.`);
    } catch (error) {
      console.log("Error during test reset:", error);
      alert('Error during test reset. Please try again.');
    }
  };

  const handleResetTotalDays = async () => {
    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      
      const streaksCollectionRef = collection(dbInstance, "streaks");
      const streakData = await getDocs(streaksCollectionRef);
      
      if (!streakData.empty) {
        const streakDoc = streakData.docs[0];
        const currentStreak = streakDoc.data().count || 0;
        const currentHistory = streakDoc.data().history || [];
        
        // Ensure total days is at least equal to current streak and history length
        const newTotalDays = Math.max(currentStreak, currentHistory.length);
        
        await updateDoc(doc(dbInstance, "streaks", streakDoc.id), {
          totalDays: newTotalDays,
          lastUpdated: new Date()
        });
        
        setTotalStreakDays(newTotalDays);
        alert(`Total days reset to ${newTotalDays} (minimum required for current streak of ${currentStreak})`);
      } else {
        alert('No streak data found to reset.');
      }
    } catch (error) {
      console.log("Error resetting total days:", error);
      alert('Error resetting total days. Please try again.');
    }
  };

  const handleResetStreak = async () => {
    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      
      const streaksCollectionRef = collection(dbInstance, "streaks");
      const streakData = await getDocs(streaksCollectionRef);
      
      if (!streakData.empty) {
        const streakDoc = streakData.docs[0];
        
        // Reset streak to 0, but keep total days as they are
        await updateDoc(doc(dbInstance, "streaks", streakDoc.id), {
          count: 0,
          lastUpdated: new Date()
        });
        
        setStreakCount(0);
        alert('Streak reset to 0 successfully!');
      } else {
        alert('No streak data found to reset.');
      }
    } catch (error) {
      console.log("Error resetting streak:", error);
      alert('Error resetting streak. Please try again.');
    }
  };

  // Fetch streak history
  const fetchStreakHistory = useCallback(async () => {
    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      
      const streaksCollectionRef = collection(dbInstance, "streaks");
      const streakData = await getDocs(streaksCollectionRef);
      
      if (!streakData.empty) {
        const streakDoc = streakData.docs[0];
        const streakDataObj = streakDoc.data();
        
        // Get the streak history (completion dates)
        const history = streakDataObj.history || [];
        const currentStreak = streakDataObj.count || 0;
        let totalDays = streakDataObj.totalDays || 0;
        
        // Ensure total days is at least equal to current streak
        totalDays = Math.max(totalDays, currentStreak);
        
        // If total days is less than the history length, use history length
        totalDays = Math.max(totalDays, history.length);
        
        setStreakHistory(history);
        setTotalStreakDays(totalDays);
      } else {
        setStreakHistory([]);
        setTotalStreakDays(0);
      }
    } catch (error) {
      console.log("Could not fetch streak history:", error);
    }
  }, []);

  // Handle streak modal open
  const handleStreakModalOpen = async () => {
    await fetchStreakHistory();
    setShowStreakModal(true);
  };

  // Handle deleting a streak day
  const handleDeleteStreakDay = async (dateToDelete) => {
    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      
      const streaksCollectionRef = collection(dbInstance, "streaks");
      const streakData = await getDocs(streaksCollectionRef);
      
      if (!streakData.empty) {
        const streakDoc = streakData.docs[0];
        const streakDataObj = streakDoc.data();
        
        // Remove the specific date from history
        const updatedHistory = streakDataObj.history?.filter(date => date !== dateToDelete) || [];
        
        // Recalculate current streak (continuous days from the most recent date)
        let currentStreak = 0;
        if (updatedHistory.length > 0) {
          const sortedDates = updatedHistory.sort((a, b) => new Date(b) - new Date(a));
          const today = new Date();
          const todayStr = today.toLocaleDateString('en-CA'); // YYYY-MM-DD format in local timezone
          
          // Check if the most recent date is today or yesterday
          const mostRecentDate = sortedDates[0];
          
          if (mostRecentDate === todayStr) {
            // Most recent date is today, start counting consecutive days
            currentStreak = 1;
            
            // Check for consecutive days before this
            for (let i = 1; i < sortedDates.length; i++) {
              const currentDate = new Date(sortedDates[i]);
              const prevDate = new Date(sortedDates[i - 1]);
              
              // Calculate day difference
              const dayDiff = Math.ceil((prevDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
              
              if (dayDiff === 1) {
                currentStreak++;
              } else {
                break;
              }
            }
          } else {
            // Check if most recent date is yesterday
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            const yesterdayStr = yesterday.toLocaleDateString('en-CA');
            
            if (mostRecentDate === yesterdayStr) {
              // Most recent date is yesterday, start counting consecutive days
              currentStreak = 1;
              
              // Check for consecutive days before this
              for (let i = 1; i < sortedDates.length; i++) {
                const currentDate = new Date(sortedDates[i]);
                const prevDate = new Date(sortedDates[i - 1]);
                
                // Calculate day difference
                const dayDiff = Math.ceil((prevDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24));
                
                if (dayDiff === 1) {
                  currentStreak++;
                } else {
                  break;
                }
              }
            }
          }
        }
        
        // Update the streak document with corrected total days
        const finalTotalDays = Math.max(updatedHistory.length, currentStreak);
        await updateDoc(doc(dbInstance, "streaks", streakDoc.id), {
          history: updatedHistory,
          totalDays: finalTotalDays,
          count: currentStreak,
          lastUpdated: new Date()
        });
        
        // Update local state
        setStreakHistory(updatedHistory);
        setTotalStreakDays(finalTotalDays);
        setStreakCount(currentStreak);
        
        alert(`Streak day ${new Date(dateToDelete).toLocaleDateString()} deleted successfully.`);
      }
    } catch (error) {
      console.log("Error deleting streak day:", error);
      alert('Error deleting streak day. Please try again.');
    }
  };

  // Format date for display
  const formatStreakDate = (dateString) => {
    // Handle YYYY-MM-DD format (local timezone)
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="planner-container">
      <div className="planner-header">
        {/* Main horizontal control row */}
        <div className="planner-controls-row">
          <div className="planner-left-section">
            <h1>Plan</h1>
            <div className="schedule-controls">
              <button
                className="schedule-toggle-btn active"
                onClick={() => {
                  const newType = scheduleType === 'weekday' ? 'weekend' : 'weekday';
                  console.log(`Button clicked! Current: ${scheduleType}, Switching to: ${newType}`);
                  handleScheduleTypeChange(newType);
                }}
                title={`Switch to ${scheduleType === 'weekday' ? 'weekend' : 'weekday'} mode`}
              >
                {scheduleType === 'weekday' ? '☕' : '🌊'}
              </button>
              <div 
                className={`streak-counter ${streakCount === 0 ? 'streak-zero' : ''}`} 
                title={`${streakCount} day${streakCount > 1 ? 's' : ''} streak!`}
                onClick={handleStreakModalOpen}
                style={{ cursor: 'pointer' }}
              >
                🔥 {streakCount}
              </div>
            </div>
          </div>
          
          <div className="planner-center-section">
            {/* Center section can be used for other content if needed */}
          </div>
          
          <div className="planner-right-section">
            <button className="add-task-btn" onClick={handleAddTaskClick}>
              + Add Task
            </button>
            <button className="birthday-btn" onClick={() => setShowBirthdayModal(true)} title="Add Birthday">🎊</button>
            <button 
              className={`planner-theme-toggle ${isDarkMode ? 'dark' : 'light'}`}
              onClick={() => setIsDarkMode(!isDarkMode)}
              title="Toggle theme"
            >
              {isDarkMode ? '☀️' : '🌙'}
            </button>
            <button 
              className="planner-nav-btn"
              onClick={() => setAppState('notes')}
              title="Go to Notes"
            >
              📝
            </button>
            <button 
              className="planner-nav-btn"
              onClick={() => setAppState('fitness')}
              title="Go to Fitness"
            >
              🏋️
            </button>
            <button 
              className="planner-exit-btn"
              onClick={handleExitClick}
              title="Exit Planner"
            >
              ✕
            </button>
          </div>
        </div>
        
        {/* Info row below */}
        <div className="planner-info-row">
          {birthdaysToday.length > 0 && (
            <div className="birthday-notification">
              {`Birthdays of Today: ${birthdaysToday.map(b => b.name).join(', ')}`}
            </div>
          )}
          
          {/* Live Counter Component */}
          {coreTaskProgress.total > 0 && (
            <div className="core-task-progress">
              {coreTaskProgress.completed === coreTaskProgress.total ? (
                <span>🎉 All core tasks done! {streakCount > 0 && `🔥 ${streakCount} day streak!`} Reset in {timeUntilReset}</span>
              ) : (
                <span>{coreTaskProgress.completed}/{coreTaskProgress.total} core tasks • Reset in {timeUntilReset}</span>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Mobile Task Edit Menu */}
      {selectedTask && !showNoteSelector && (
        <div className="mobile-task-edit-overlay" onClick={() => setSelectedTask(null)}>
          <div className="mobile-task-edit-menu" onClick={(e) => e.stopPropagation()}>
            <div className="task-edit-header">
              <h3>Task Options</h3>
              <button className="close-btn" onClick={() => setSelectedTask(null)}>×</button>
            </div>
            <div className="task-edit-actions">
              <button onClick={() => handleEditTask(selectedTask, 'rename')}>
                <span className="action-icon">✏️</span>
                <span className="action-text">Edit</span>
              </button>
              <button onClick={() => handleEditTask(selectedTask, 'linkNote')}>
                <span className="action-icon">📝</span>
                <span className="action-text">Link Note</span>
              </button>
              <button onClick={handleGoToLinkedNote}>
                <span className="action-icon">🔗</span>
                <span className="action-text">Go To Linked Note</span>
              </button>
              <button onClick={() => handleEditTask(selectedTask, 'delete')} className="delete-action">
                <span className="action-icon">🗑️</span>
                <span className="action-text">Delete</span>
              </button>
              <button onClick={() => setSelectedTask(null)} className="cancel-action">
                <span className="action-text">Cancel</span>
              </button>
            </div>
          </div>
        </div>
      )}


      
      
      {/* Add/Edit Task Modal */}
      {showAddTaskModal && (
        <div className="add-task-modal-overlay">
          <div className="add-task-modal">
            <form onSubmit={handleAddTaskSubmit}>
              <h2>{editTaskId ? 'Edit Task' : 'Add Task'}</h2>
              <label>Title:</label>
              <input
                type="text"
                value={newTaskTitle}
                onChange={e => setNewTaskTitle(e.target.value)}
                style={{ width: '100%', fontSize: '1.1rem', marginBottom: 8 }}
                placeholder="Task name"
              />
              <label>Start Time:</label>
              <input
                type="time"
                value={newTaskStart}
                onChange={e => setNewTaskStart(e.target.value)}
                required
                style={{ width: '100%', fontSize: '1.1rem', marginBottom: 8 }}
              />
              <label>End Time:</label>
              <input
                type="time"
                value={newTaskEnd}
                onChange={e => setNewTaskEnd(e.target.value)}
                required
                style={{ width: '100%', fontSize: '1.1rem', marginBottom: 8 }}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={newTaskCompleted}
                  onChange={e => setNewTaskCompleted(e.target.checked)}
                  style={{ width: '16px', height: '16px', margin: 0 }}
                />
                <span>Mark as completed</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 8 }}>
                <input
                  type="checkbox"
                  checked={newTaskCore}
                  onChange={e => setNewTaskCore(e.target.checked)}
                  style={{ width: '16px', height: '16px', margin: 0 }}
                />
                <span>Core Node</span>
              </label>
              {addTaskError && <div style={{ color: '#ff6b6b', marginBottom: 8 }}>{addTaskError}</div>}
              <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
                <button type="submit" className="add-task-btn" disabled={isAddingTask}>
                  {isAddingTask ? (editTaskId ? 'Saving...' : 'Adding...') : (editTaskId ? 'Save' : 'Add')}
                </button>
                <button type="button" className="add-task-btn" onClick={() => { setShowAddTaskModal(false); setEditTaskId(null); }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="planner-canvas" ref={canvasOuterRef} style={{overflow: 'hidden', height: VISIBLE_HOURS * HOUR_HEIGHT}}>
        <div
          className="planner-scroll-window"
          ref={scrollContainerRef}
          style={{
            display: 'flex',
            flexDirection: 'row',
            height: '100%',
            overflowY: 'auto',
            scrollBehavior: 'smooth',
            width: '100%'
          }}
          onScroll={handleScroll}
        >
          <div className="time-ruler" style={{minWidth: 80, width: 80, height: fullScrollHeight, position: 'relative'}}>
            {hourSlots.map((hour, idx) => {
              const isCurrentHour = hour.getHours() === currentTime.getHours();
              const isDragOver = dragOverHour === hour.getTime();
            return (
              <div 
                  key={hour.toISOString()}
                  className={`time-mark${isCurrentHour ? ' current' : ''}${isDragOver ? ' drag-over' : ''}`}
                  style={{
                    height: HOUR_HEIGHT,
                    minHeight: HOUR_HEIGHT,
                    maxHeight: HOUR_HEIGHT,
                    fontSize: '1.2rem',
                    background: isDragOver ? '#be00ff33' : undefined,
                    position: 'absolute',
                    top: idx * HOUR_HEIGHT,
                    left: 0,
                    right: 0,
                    width: '100%'
                  }}
                  onDragOver={draggedTaskId ? e => handleDragOver(hour, e) : undefined}
                  onDrop={draggedTaskId ? e => handleDrop(hour, e) : undefined}
                >
                  {hour.toLocaleTimeString('en-US', {
                  hour: 'numeric', 
                  hour12: true 
                })}
              </div>
            );
          })}
        </div>
          <div className="tasks-hourly-list" style={{flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', height: fullScrollHeight}}>
            {/* Render all tasks, position and size based on time */}
            {console.log(`Rendering ${sortedTasks.length} tasks, taskPositions:`, taskPositions)}
            {sortedTasks.map((task, idx) => {
              const segments = taskPositions[idx];
              console.log(`Task ${task.title} (idx ${idx}) has ${segments ? segments.length : 0} segments:`, segments);
              
              // Fallback: if no segments were created, create a default segment
              if (!segments || segments.length === 0) {
                console.log(`Creating fallback segment for task ${task.title}`);
                const taskStart = new Date(task.startTime);
                const taskEnd = new Date(task.endTime);
                const baselineTime = new Date(taskStart.getFullYear(), taskStart.getMonth(), taskStart.getDate(), 5, 0, 0, 0);
                const timeOffset = taskStart.getTime() - baselineTime.getTime();
                const top = Math.max(0, (timeOffset / (60 * 60 * 1000)) * HOUR_HEIGHT);
                const durationMs = taskEnd.getTime() - taskStart.getTime();
                const durationHours = durationMs / (60 * 60 * 1000);
                const height = durationHours * HOUR_HEIGHT;

                const fallbackSegment = {
                  id: `${task.id}-fallback`,
                  top,
                  height,
                  displayStart: taskStart,
                  displayEnd: taskEnd,
                  isFirstSegment: true
                };
                
                return [fallbackSegment].map((segment, segmentIdx) => {
              const isCurrent = currentTask && task.id === currentTask.id;
              const linkedNote = notes.find(note => note.id === task.noteId);
              const isSelected = selectedTask && selectedTask.id === task.id;
                  const { top, height, displayStart, displayEnd } = segment;
                  const isCompleted = task.completed || false;
                  
                  console.log(`Rendering fallback segment for ${task.title} at position ${top}, height ${height}`);
              return (
                <div
                      key={segment.id}
                      className={`task-node task-node-compact${isCurrent ? ' current' : ''}${draggedTaskId === task.id ? ' dragging' : ''}${isSelected ? ' selected' : ''}${isCompleted ? ' completed' : ''}`}
                  style={{
                    margin: '0 auto',
                    width: '80%',
                    minWidth: 10,
                    maxWidth: 600,
                    minHeight: 0,
                    height: height,
                    padding: height < 28 ? '1px 12px' : height < 60 ? '4px 14px' : '12px 18px',
                    fontSize: height < 28 ? 12 : height < 60 ? 14 : 18,
                    overflow: 'hidden',
                    opacity: draggedTaskId === task.id ? 0.5 : 1,
                    cursor: 'pointer',
                    position: 'absolute',
                    top: top,
                    left: 0,
                    right: 0,
                    boxSizing: 'border-box',
                    border: isSelected ? '2px solid #be00ff' : undefined,
                    background: isSelected ? 'rgba(190,0,255,0.08)' : undefined,
                    outline: isSelected ? '2px solid #be00ff' : undefined,
                    zIndex: isSelected ? 2 : 1,
                    filter: isCompleted ? 'grayscale(0.6) brightness(0.7)' : undefined,
                  }}
                  tabIndex={0}
                  onClick={(e) => handleTaskClick(e, task)}
                  onContextMenu={(e) => { e.preventDefault(); deleteTask(task.id); }}
                  onFocus={() => setSelectedTask(task)}
                >
                  <div className="task-content">
                        <div className="task-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span style={{ textDecoration: isCompleted ? 'line-through' : 'none' }}>
                            {task.core && <span style={{ marginRight: '4px', color: '#ff6b35' }}>⭐</span>}
                            {task.title}
                          </span>
                          <TaskCheckbox task={task} isCompleted={isCompleted} />
                        </div>
                    <div className="task-time">
                          {formatTime(displayStart.getTime())} - {formatTime(displayEnd.getTime())}
                          {linkedNote && (
                            <span className="task-note-link">📝 {linkedNote.title}</span>
                          )}
                    </div>
                  </div>
                </div>
              );
                });
              }
              
              return segments.map((segment, segmentIdx) => {
                const isCurrent = currentTask && task.id === currentTask.id;
                const linkedNote = notes.find(note => note.id === task.noteId);
                const isSelected = selectedTask && selectedTask.id === task.id;
                const { top, height, displayStart, displayEnd } = segment;
                const isCompleted = task.completed || false;
                
                console.log(`Rendering task segment ${task.title} at position ${top}, height ${height}`);
                return (
                <div
                  key={segment.id}
                  className={`task-node task-node-compact${isCurrent ? ' current' : ''}${draggedTaskId === task.id ? ' dragging' : ''}${isSelected ? ' selected' : ''}${isCompleted ? ' completed' : ''}`}
                  style={{
                    margin: '0 auto',
                    width: '80%',
                    minWidth: 10,
                    maxWidth: 600,
                    minHeight: 0,
                    height: height,
                    padding: height < 28 ? '1px 12px' : height < 60 ? '4px 14px' : '12px 18px',
                    fontSize: height < 28 ? 12 : height < 60 ? 14 : 18,
                    overflow: 'hidden',
                    opacity: draggedTaskId === task.id ? 0.5 : 1,
                    cursor: 'pointer',
                    position: 'absolute',
                    top: top,
                    left: 0,
                    right: 0,
                    boxSizing: 'border-box',
                    border: isSelected ? '2px solid #be00ff' : undefined,
                    background: isSelected ? 'rgba(190,0,255,0.08)' : undefined,
                    outline: isSelected ? '2px solid #be00ff' : undefined,
                    zIndex: isSelected ? 2 : 1,
                    filter: isCompleted ? 'grayscale(0.6) brightness(0.7)' : undefined,
                    // Add visual indicator for wrapped tasks
                    borderLeft: !segment.isFirstSegment ? '4px solid #be00ff' : undefined,
                  }}
                  tabIndex={0}
                  onClick={(e) => handleTaskClick(e, task)}
                  onContextMenu={(e) => { e.preventDefault(); deleteTask(task.id); }}
                  onFocus={() => setSelectedTask(task)}
                >
                  <div className="task-content">
                    <div className="task-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ textDecoration: isCompleted ? 'line-through' : 'none' }}>
                        {task.core && <span style={{ marginRight: '4px', color: '#ff6b35' }}>⭐</span>}
                        {task.title}{!segment.isFirstSegment ? ' (continued)' : ''}
                      </span>
                      <TaskCheckbox task={task} isCompleted={isCompleted} />
                    </div>
                    <div className="task-time">
                      {formatTime(displayStart.getTime())} - {formatTime(displayEnd.getTime())}
                      {task.startTime !== displayStart.getTime() || task.endTime !== displayEnd.getTime() ?
                        ` (original: ${formatTime(task.startTime)} - ${formatTime(task.endTime)})` : ''}
                      {linkedNote && (
                        <span className="task-note-link">📝 {linkedNote.title}</span>
                      )}
                    </div>
                  </div>
                </div>
                );
              });
            })}
            {/* Position container relative for absolute task nodes */}
            <div style={{position: 'relative', height: fullScrollHeight}} />
          </div>
        </div>
      </div>



      {showNoteSelector && (
        <div className="note-selector-overlay">
          <div className="note-selector">
            <h3>Select a Note</h3>
            <div className="note-list">
              {notes.map(note => (
                <button
                  key={note.id}
                  className="note-option"
                  onClick={() => handleNoteSelect(note.id)}
                >
                  {note.title}
                </button>
              ))}
            </div>
            <button 
              className="cancel-btn"
              onClick={() => {
                setShowNoteSelector(false);
                setSelectedTask(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}



      {/* Edit Task Modal */}
      {showEditTaskModal && (
        <div className="edit-task-modal-overlay">
          <div className="edit-task-modal">
            <form onSubmit={handleEditTaskModalSubmit}>
              <h2>Edit Task Name</h2>
              <input
                type="text"
                value={editTaskName}
                onChange={e => setEditTaskName(e.target.value)}
                autoFocus
                style={{ fontSize: '1.1rem', padding: '8px', borderRadius: 6, border: '1px solid #be00ff', width: '100%', marginBottom: 16 }}
              />
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button type="submit" className="add-task-btn">Save</button>
                <button type="button" className="add-task-btn" onClick={() => setShowEditTaskModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showBirthdayModal && (
        <div className="note-selector-overlay">
          <div className="note-selector birthday-modal">
            <div className="birthday-modal-header">
              <h3>Birthdays</h3>
              <button
                className="birthday-close-btn"
                onClick={() => {
                  setShowBirthdayModal(false);
                  setBirthdaySearch('');
                  setShowAddBirthdayForm(false);
                }}
              >
                ×
              </button>
            </div>

            {/* Search Bar */}
            <div className="birthday-search-container">
              <input
                type="text"
                placeholder="Search birthdays..."
                value={birthdaySearch}
                onChange={e => setBirthdaySearch(e.target.value)}
                className="birthday-search-input"
                autoFocus
              />
              <button
                className="birthday-add-toggle"
                onClick={() => setShowAddBirthdayForm(!showAddBirthdayForm)}
                title={showAddBirthdayForm ? "Cancel" : "Add Birthday"}
              >
                {showAddBirthdayForm ? '×' : '+'}
              </button>
            </div>

            {/* Add Birthday Form (collapsible) */}
            {showAddBirthdayForm && (
              <form onSubmit={(e) => {
                handleAddBirthday(e);
                setShowAddBirthdayForm(false);
              }} className="birthday-add-form">
                <input
                  type="text"
                  placeholder="Name"
                  value={birthdayName}
                  onChange={e => setBirthdayName(e.target.value)}
                  className="birthday-input"
                  required
                />
                <input
                  type="date"
                  value={birthdayDate}
                  onChange={e => setBirthdayDate(e.target.value)}
                  className="birthday-input"
                  required
                />
                <button type="submit" className="birthday-submit-btn">Add</button>
              </form>
            )}

            {/* Birthday List */}
            <div className="birthday-list">
              {birthdays.length === 0 && (
                <div className="birthday-empty">No birthdays added yet.</div>
              )}
              {birthdays
                .filter(b => b.name.toLowerCase().includes(birthdaySearch.toLowerCase()))
                .sort((a, b) => {
                  // Sort by upcoming date
                  const today = new Date();
                  const getNextBirthday = (dateStr) => {
                    const [, month, day] = dateStr.split('-');
                    const bday = new Date(today.getFullYear(), parseInt(month) - 1, parseInt(day));
                    if (bday < today) bday.setFullYear(today.getFullYear() + 1);
                    return bday;
                  };
                  return getNextBirthday(a.date) - getNextBirthday(b.date);
                })
                .map((b, i) => {
                  const [, month, day] = b.date.split('-');
                  const formattedDate = `${month}/${day}`;
                  const today = new Date();
                  const isToday = today.getMonth() + 1 === parseInt(month) && today.getDate() === parseInt(day);
                  return (
                    <div key={b.id || i} className={`birthday-item ${isToday ? 'birthday-today' : ''}`}>
                      <div className="birthday-item-info">
                        <span className="birthday-item-name">{b.name}</span>
                        <span className="birthday-item-date">{formattedDate}</span>
                      </div>
                      <button
                        onClick={() => handleDeleteBirthday(b)}
                        className="birthday-delete-btn"
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  );
                })}
              {birthdays.length > 0 && birthdays.filter(b => b.name.toLowerCase().includes(birthdaySearch.toLowerCase())).length === 0 && (
                <div className="birthday-empty">No matches found.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Exit Confirmation Modal */}
      {showExitConfirmation && (
        <div className="exit-confirmation-overlay">
          <div className="exit-confirmation">
            <h3>Exit Planner</h3>
            <p>Are you sure you want to exit?</p>
            <div className="modal-buttons">
              <button onClick={handleExitConfirm}>Yes</button>
              <button onClick={handleExitCancel}>No</button>
            </div>
          </div>
        </div>
      )}

             {/* Streak History Modal */}
       {showStreakModal && (
         <div className="streak-history-overlay" onClick={() => setShowStreakModal(false)}>
           <div className="streak-history-modal" onClick={(e) => e.stopPropagation()}>
             <div className="streak-history-header">
               <h3>Streak History</h3>
               <button className="close-btn" onClick={() => setShowStreakModal(false)}>×</button>
             </div>
             <div className="streak-history-content">
               {/* Action Buttons Section */}
               <div className="streak-actions-top">
                 <button 
                   className="reset-btn test-btn" 
                   onClick={handleTestReset} 
                   title="Test: Reset Core Tasks & Increment Streak"
                 >
                   Test
                 </button>
                 <button 
                   className="reset-btn total-days-btn" 
                   onClick={handleResetTotalDays} 
                   title="Reset Total Days to 0"
                 >
                   Reset Total Days
                 </button>
                 <button 
                   className="reset-btn streak-btn" 
                   onClick={handleResetStreak} 
                   title="Reset Streak to 0"
                 >
                   Reset Streak
                 </button>
               </div>
               
               {/* Streak Summary Section */}
               <div className="streak-summary-enhanced">
                 <div className="streak-stat-card">
                   <div className="streak-stat-content">
                     <span className="streak-label">Current Streak</span>
                     <span className="streak-value">{streakCount} day{streakCount !== 1 ? 's' : ''}</span>
                   </div>
                 </div>
                 <div className="streak-stat-card">
                   <div className="streak-stat-content">
                     <span className="streak-label">Total Days</span>
                     <span className="streak-value">{totalStreakDays} day{totalStreakDays !== 1 ? 's' : ''}</span>
                   </div>
                 </div>
               </div>
               
               {/* Streak History Section */}
               <div className="streak-history-list">
                 <h4>Completed Days</h4>
                 <div className="streak-history-scroll">
                   {streakHistory.length > 0 ? (
                     <ul className="streak-days-list">
                       {streakHistory
                         .sort((a, b) => new Date(b) - new Date(a))
                         .map((date, index) => (
                           <li key={index} className="streak-day-item">
                             <span className="streak-day-date">{formatStreakDate(date)}</span>
                             <button 
                               className="delete-streak-day-btn"
                               onClick={() => handleDeleteStreakDay(date)}
                               title="Delete this streak day"
                             >
                               ×
                             </button>
                           </li>
                         ))}
                     </ul>
                   ) : (
                     <p className="no-streak-history">No streak history available.</p>
                   )}
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}

    </div>
  );
};

const NotesAppView = ({ setAppState, user, isDarkMode, setIsDarkMode, initialNoteId, setInitialNoteId }) => {
  const [notes, setNotes] = useState([]);
  const [activeNote, setActiveNote] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true); // Start with sidebar open
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const sidebarRef = useRef(null);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  
  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Password protection states for individual notes
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [noteToUnlock, setNoteToUnlock] = useState(null);
  const [unlockedNoteId, setUnlockedNoteId] = useState(null);
  
  // Note menu states
  const [showNoteMenu, setShowNoteMenu] = useState(false);

  const [selectedNoteIndex, setSelectedNoteIndex] = useState(null);

  // Formatting popup states
  const [showFormattingPopup, setShowFormattingPopup] = useState(false);
  const [currentEditor, setCurrentEditor] = useState(null);

  // Exit confirmation popup state
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);

  // ESC key handlers for modals
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        // Close modals in order of priority
        if (showPasswordModal) {
          setShowPasswordModal(false);
          setPasswordError('');
          setPassword('');
          setNoteToUnlock(null);
          setActiveNote(null);
        } else if (showFormattingPopup) {
          setShowFormattingPopup(false);
          setCurrentEditor(null);
        } else if (showNoteMenu) {
          setShowNoteMenu(false);
          setSelectedNoteIndex(null);
        } else if (showExitConfirmation) {
          setShowExitConfirmation(false);
        }
      }
    };

    // Only add listener if any modal is open
    if (showPasswordModal || showFormattingPopup || showNoteMenu || showExitConfirmation) {
      window.addEventListener('keydown', handleEscKey);
      return () => window.removeEventListener('keydown', handleEscKey);
    }
  }, [showPasswordModal, showFormattingPopup, showNoteMenu, showExitConfirmation]);

  // Exit confirmation handlers
  const handleExitClick = () => {
    setShowExitConfirmation(true);
  };

  const handleExitConfirm = () => {
    setShowExitConfirmation(false);
    setAppState('main');
  };

  const handleExitCancel = () => {
    setShowExitConfirmation(false);
  };

  // Password verification
  const verifyPassword = (inputPassword) => {
    return inputPassword === '4568';
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (verifyPassword(password)) {
      setShowPasswordModal(false);
      setPasswordError('');
      setPassword('');
      // Unlock the specific note and track it
      if (noteToUnlock) {
        setActiveNote(noteToUnlock);
        setUnlockedNoteId(noteToUnlock.id);
        setNoteToUnlock(null);
      }
    } else {
      setPasswordError('Incorrect password');
      setPassword('');
    }
  };

  const handlePasswordCancel = () => {
    setShowPasswordModal(false);
    setPasswordError('');
    setPassword('');
    setNoteToUnlock(null);
    setActiveNote(null); // Return to notes selection
  };

  const handleNoteClick = (note) => {
    // Check if note is private - always require password for private notes
    if (note.isPrivate) {
      setNoteToUnlock(note);
      setShowPasswordModal(true);
      return;
    }
    setActiveNote(note);
  };

  // Handle formatting button click
  const handleFormattingButtonClick = () => {
    setShowFormattingPopup(true);
  };

  // Close formatting popup
  const handleCloseFormattingPopup = () => {
    setShowFormattingPopup(false);
    setCurrentEditor(null);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowNoteMenu(null);
    };
    
    if (showNoteMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showNoteMenu]);

  const getNotes = useCallback(async () => {
    if (!user) return;
    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      const notesCollectionRef = collection(dbInstance, "notes");
      const data = await getDocs(notesCollectionRef);
      const sortedNotes = data.docs
        .map((doc) => ({ ...doc.data(), id: doc.id }))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setNotes(sortedNotes);
    } catch (error) {
      console.log("Could not fetch notes:", error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      getNotes();
    }
  }, [getNotes, user]);

  useEffect(() => {
    if (initialNoteId && notes.length > 0) {
      const note = notes.find(n => n.id === initialNoteId);
      if (note) {
        setActiveNote(note);
        setSelectedNoteId(note.id);
      }
      setInitialNoteId(null);
    }
  }, [initialNoteId, notes, setInitialNoteId]);

  // Touch event handlers for mobile swipe
  // const handleTouchStart = (e) => {
  //   touchStartX.current = e.touches[0].clientX;
  //   touchStartY.current = e.touches[0].clientY;
  // };

  // const handleTouchMove = (e) => {
  //   // Only prevent default if it's clearly a swipe gesture
  //   const currentX = e.touches[0].clientX;
  //   const currentY = e.touches[0].clientY;
  //   const deltaX = currentX - touchStartX.current;
  //   const deltaY = currentY - touchStartY.current;
    
  //   // If it's a clear horizontal swipe from the left edge, prevent default
  //   if (Math.abs(deltaX) > Math.abs(deltaY) && 
  //       Math.abs(deltaX) > 30 && 
  //       touchStartX.current < 80) {
  //     e.preventDefault();
  //   }
  // };

  // const handleTouchEnd = (e) => {
  //   const touchEndX = e.changedTouches[0].clientX;
  //   const touchEndY = e.changedTouches[0].clientY;
  //   const deltaX = touchEndX - touchStartX.current;
  //   const deltaY = touchEndY - touchStartY.current;
    
  //   // Check if it's a horizontal swipe (more horizontal than vertical)
  //   if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
  //     if (deltaX > 0 && touchStartX.current < 80) {
  //       // Swipe right from left edge - open sidebar
  //       setIsSidebarOpen(true);
  //     } else if (deltaX < 0 && isSidebarOpen) {
  //       // Swipe left when sidebar is open - close sidebar
  //       setIsSidebarOpen(false);
  //     }
  //   }
    
  //   // Also allow tapping anywhere on the right side when sidebar is open to close it
  //   if (isSidebarOpen && touchEndX > 280) {
  //     if (selectedNoteId) {
  //       const selectedNote = notes.find(note => note.id === selectedNoteId);
  //       if (selectedNote) {
  //         setActiveNote(selectedNote);
  //       }
  //     }
  //     setIsSidebarOpen(false);
  //     setSelectedNoteId(null);
  //   }
  // };

  // const handleContainerClick = (e) => {
  //   // If clicking outside the sidebar, open the selected note and close sidebar
  //   if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
  //     if (selectedNoteId) {
  //       const selectedNote = notes.find(note => note.id === selectedNoteId);
  //       if (selectedNote) {
  //         setActiveNote(selectedNote);
  //       }
  //     }
  //     setIsSidebarOpen(false);
  //     setSelectedNoteId(null);
  //   }
  // };

  const createNote = async () => {
    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      const notesCollectionRef = collection(dbInstance, "notes");
      const maxOrder = notes.length > 0 ? Math.max(...notes.map(n => n.order ?? 0)) : 0;
      const newNote = {
        title: "Untitled Note",
        content: "",
        createdAt: new Date(),
        order: maxOrder + 1
      };
      const docRef = await addDoc(notesCollectionRef, newNote);
      await getNotes();
      setActiveNote({ ...newNote, id: docRef.id });
    } catch (error) {
      console.log("Could not create note:", error);
    }
  };

  const deleteNote = async (id) => {
    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      const noteDoc = doc(dbInstance, "notes", id);
      await deleteDoc(noteDoc);
      setActiveNote(null);
      getNotes();
    } catch (error) {
      console.log("Could not delete note:", error);
    }
  };

  const updateNote = async (id, updatedData) => {
    try {
      const dbInstance = getDbInstance();
      if (!dbInstance) return;
      const noteDoc = doc(dbInstance, "notes", id);
      await updateDoc(noteDoc, updatedData);
      getNotes();
    } catch (error) {
      console.log("Could not update note:", error);
    }
  };



  // Note menu handlers
  const handleNoteMenuClick = (e, noteId, index) => {
    e.stopPropagation();
    setShowNoteMenu(true);
    setSelectedNoteId(noteId);
    setSelectedNoteIndex(index);
  };

  const handleMenuAction = (action) => {
    if (selectedNoteId === null || selectedNoteIndex === null) return;
    
    const noteId = selectedNoteId;
    const index = selectedNoteIndex;
    
    switch (action) {
      case 'moveUp':
        if (index > 0) {
          const newNotes = [...notes];
          [newNotes[index], newNotes[index - 1]] = [newNotes[index - 1], newNotes[index]];
          setNotes(newNotes);
          // Update in database
          newNotes.forEach((note, idx) => {
            updateNote(note.id, { order: idx });
          });
        }
        break;
      case 'moveDown':
        if (index < notes.length - 1) {
          const newNotes = [...notes];
          [newNotes[index], newNotes[index + 1]] = [newNotes[index + 1], newNotes[index]];
          setNotes(newNotes);
          // Update in database
          newNotes.forEach((note, idx) => {
            updateNote(note.id, { order: idx });
          });
        }
        break;
      case 'rename':
        const newTitle = prompt('Enter new title:', notes[index].title);
        if (newTitle && newTitle.trim()) {
          updateNote(noteId, { title: newTitle.trim() });
          setNotes(prev => prev.map(note => 
            note.id === noteId ? { ...note, title: newTitle.trim() } : note
          ));
        }
        break;
      case 'delete':
        if (window.confirm('Are you sure you want to delete this note?')) {
          deleteNote(noteId);
          if (activeNote && activeNote.id === noteId) {
            setActiveNote(null);
          }
        }
        break;
      case 'makePrivate':
        updateNote(noteId, { isPrivate: true });
        setNotes(prev => prev.map(note => 
          note.id === noteId ? { ...note, isPrivate: true } : note
        ));
        break;
      case 'makePublic':
        updateNote(noteId, { isPrivate: false });
        setNotes(prev => prev.map(note => 
          note.id === noteId ? { ...note, isPrivate: false } : note
        ));
        break;
      default:
        break;
    }
    setShowNoteMenu(false);
    setSelectedNoteId(null);
    setSelectedNoteIndex(null);
  };

  return (
    <div 
      className={`notes-app-container${isMobile ? ' mobile' : ''}${isSidebarOpen ? ' sidebar-open' : ''}`}
    >
      {/* Password Modal */}
      {showPasswordModal && (
        <div className="password-modal-overlay">
          <div className="password-modal">
            <h3>Note Protected</h3>
            <p>Enter password to unlock this note:</p>
            <form onSubmit={handlePasswordSubmit}>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoFocus
              />
              {passwordError && <p className="error">{passwordError}</p>}
              <div className="modal-buttons">
                <button type="submit">Unlock</button>
                <button type="button" onClick={handlePasswordCancel} className="cancel-btn">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Note Selector - Full screen on mobile when sidebar is open */}
      <div 
        className={`notes-sidebar${isSidebarOpen ? ' sidebar-open' : ''}`}
        ref={sidebarRef}
        style={isMobile && isSidebarOpen ? {
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1000,
          background: 'inherit'
        } : {}}
      >
        {/* Mobile header for note selector */}
        {isMobile && isSidebarOpen && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px',
            borderBottom: '1px solid rgba(120, 119, 198, 0.2)',
            background: 'rgba(120, 119, 198, 0.05)'
          }}>
            <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#e0e0e0' }}>Notes</h2>
            <button
              onClick={() => setIsSidebarOpen(false)}
              style={{
                background: 'none',
                border: 'none',
                color: '#be00ff',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>
          </div>
        )}
        
        <button className="new-note-btn" onClick={createNote}>+ New Note</button>
        <ul className="notes-list-sidebar">
          {notes.map((note, idx) => (
            <li 
              key={note.id} 
              className={
                (activeNote?.id === note.id ? 'active ' : '') +
                (selectedNoteId === note.id ? 'selected ' : '') +
                'note-item'
              }
              onClick={() => {
                handleNoteClick(note);
                // On mobile, close the selector and open the editor when a note is selected
                if (isMobile) {
                  setIsSidebarOpen(false);
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                <span className="note-title">{note.title}</span>
                {note.isPrivate && (
                  <span style={{ color: '#be00ff', fontSize: '14px' }}>🔒</span>
                )}
              </div>
              <button
                className="note-menu-btn"
                onClick={(e) => handleNoteMenuClick(e, note.id, idx)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#be00ff',
                  cursor: 'pointer',
                  fontSize: '18px',
                  padding: '4px',
                  borderRadius: '4px',
                  marginLeft: 'auto'
                }}
                title="Note actions"
              >
                ✏️
              </button>
            </li>
          ))}
          {/* Add padding below the last note for better mobile experience */}
          <li style={{ 
            height: isMobile ? '120px' : '40px', 
            listStyle: 'none',
            margin: 0,
            padding: 0
          }}></li>
        </ul>
      </div>
              {/* Note Editor - Full screen on mobile when sidebar is closed */}
      <div 
        className={`note-editor ${isSidebarOpen ? 'mobile-editor' : ''}`} 
        style={{ 
          width: '100%', 
          flex: 1, 
          alignSelf: 'stretch',
          ...(isMobile && !isSidebarOpen ? {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1000,
            background: 'inherit'
          } : {})
        }}
      >
        {/* Desktop header for note editor */}
        {!isMobile && activeNote && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px 10px 20px',
            marginBottom: '10px'
          }}>
            <h2 style={{ 
              margin: 0, 
              fontSize: '1.4rem', 
              color: '#e0e0e0',
              fontWeight: '600',
              borderBottom: '2px solid #be00ff',
              paddingBottom: '8px'
            }}>
              {activeNote.title}
            </h2>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {activeNote.isPrivate && (
                <span style={{ 
                  color: '#be00ff', 
                  fontSize: '16px',
                  marginRight: '8px'
                }}>🔒</span>
              )}
            </div>
          </div>
        )}
        
        {/* Mobile header for note editor */}
        {isMobile && !isSidebarOpen && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px 20px',
            borderBottom: '1px solid rgba(120, 119, 198, 0.2)',
            background: 'rgba(120, 119, 198, 0.05)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flex: 1,
              minWidth: 0
            }}>
              <button
                onClick={() => setIsSidebarOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#be00ff',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                ☰
              </button>
              <h2 style={{ 
                margin: 0, 
                fontSize: '1.2rem', 
                color: '#e0e0e0',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {activeNote ? activeNote.title : 'No Note Selected'}
              </h2>
            </div>
            <button
              onClick={handleExitClick}
              style={{
                background: 'none',
                border: 'none',
                color: '#be00ff',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              ✕
            </button>
          </div>
        )}
        
        {activeNote ? (
          <div style={{ position: 'relative' }}>
            {activeNote.isPrivate && unlockedNoteId !== activeNote.id ? (
              <div style={{ 
                filter: 'blur(10px)', 
                pointerEvents: 'none',
                position: 'relative'
              }}>
                <TiptapEditor
                  key={activeNote?.id}
                  content={activeNote.content}
                  onContentChange={(newContent) => {
                    setActiveNote(prev => ({ ...prev, content: newContent }));
                    // Debounced save
                    const timer = setTimeout(() => {
                      updateNote(activeNote.id, { content: newContent });
                    }, 500);
                    return () => clearTimeout(timer);
                  }}
                  onEditorReady={(editor) => {
                    setCurrentEditor(editor);
                  }}
                />
              </div>
            ) : (
              <TiptapEditor
                key={activeNote?.id}
                content={activeNote.content}
                onContentChange={(newContent) => {
                  setActiveNote(prev => ({ ...prev, content: newContent }));
                  // Debounced save
                  const timer = setTimeout(() => {
                    updateNote(activeNote.id, { content: newContent });
                  }, 500);
                  return () => clearTimeout(timer);
                }}
                onEditorReady={(editor) => {
                  setCurrentEditor(editor);
                }}
              />
            )}
            {activeNote.isPrivate && unlockedNoteId !== activeNote.id && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                background: 'rgba(0, 0, 0, 0.8)',
                color: '#be00ff',
                padding: '20px',
                borderRadius: '8px',
                textAlign: 'center',
                zIndex: 10
              }}>
                <div style={{ fontSize: '24px', marginBottom: '10px' }}>🔒</div>
                <div>This note is private</div>
                <div style={{ fontSize: '14px', marginTop: '5px', opacity: 0.8 }}>
                  Enter password to unlock
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="no-note-selected">
            <h2>Select a note to edit or create a new one.</h2>
          </div>
        )}
        {/* Exit icon in top right corner - only show on desktop */}
        {!isMobile && (
          <div style={{
            position: 'absolute',
            top: 24,
            right: 24,
            zIndex: 10
          }}>
            <button
              className="exit-notes-icon"
              onClick={handleExitClick}
              title="Exit Notes"
              style={{
                fontSize: 20,
                borderRadius: '50%',
                width: 44,
                height: 44,
                border: '1px solid #be00ff',
                background: 'transparent',
                color: '#be00ff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s'
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Floating controls: sidebar toggle and theme toggle */}
        <div style={{
          position: 'fixed',
          bottom: isMobile ? 16 : 24,
          right: isMobile ? 16 : 24,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: isMobile ? 8 : 12,
          zIndex: 1000
        }}>
          {/* Sidebar toggle button - only show on desktop */}
          {!isMobile && (
            <button
              className="sidebar-toggle-btn"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              title={isSidebarOpen ? 'Close Sidebar' : 'Open Sidebar'}
              style={{
                fontSize: 24,
                borderRadius: '50%',
                width: 44,
                height: 44,
                border: '1px solid #be00ff',
                background: isSidebarOpen ? '#be00ff22' : '#222',
                color: '#be00ff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                marginBottom: 4,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background 0.2s'
              }}
            >
              {isSidebarOpen ? '←' : '☰'}
            </button>
          )}
          {/* Formatting button */}
          <button
            className="formatting-btn"
            onClick={handleFormattingButtonClick}
            title="Text Formatting"
            style={{
              fontSize: isMobile ? 14 : 20,
              borderRadius: '50%',
              width: isMobile ? 36 : 44,
              height: isMobile ? 36 : 44,
              border: '1px solid #be00ff',
              background: showFormattingPopup ? '#be00ff22' : 'transparent',
              color: '#be00ff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              marginBottom: 4,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s',
              lineHeight: 1,
              padding: 0
            }}
          >
            📏
          </button>
          {/* Theme toggle button */}
          <button 
            className={`notes-theme-toggle-btn ${isDarkMode ? 'dark' : 'light'}`}
            onClick={() => setIsDarkMode(!isDarkMode)}
            title="Toggle theme"
            style={{
              fontSize: isMobile ? 16 : 24,
              borderRadius: '50%',
              width: isMobile ? 36 : 44,
              height: isMobile ? 36 : 44,
              border: '1px solid #be00ff',
              background: isDarkMode ? '#222' : '#fff',
              color: isDarkMode ? '#be00ff' : '#be00ff',
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s',
              lineHeight: 1,
              padding: 0
            }}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>
          {/* Switch to Planner button */}
          <button
            className="notes-to-planner-btn"
            onClick={() => setAppState('planner')}
            title="Back to Planner"
            style={{
              marginLeft: 0,
              border: '1px solid var(--secondary-glow)',
              borderRadius: '50%',
              background: 'transparent',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: isMobile ? 14 : 20,
              width: isMobile ? 36 : 44,
              height: isMobile ? 36 : 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 4,
              lineHeight: 1,
              padding: 0
            }}
          >
            📅
          </button>
          {/* Switch to Fitness button */}
          <button
            className="notes-to-planner-btn"
            onClick={() => setAppState('fitness')}
            title="Go to Fitness"
            style={{
              marginLeft: 0,
              border: '1px solid var(--secondary-glow)',
              borderRadius: '50%',
              background: 'transparent',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: isMobile ? 14 : 20,
              width: isMobile ? 36 : 44,
              height: isMobile ? 36 : 44,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 4,
              lineHeight: 1,
              padding: 0
            }}
          >
            🏋️
          </button>
      </div>
      </div>

      {/* Note Menu */}
      {showNoteMenu && (
        <div className="note-menu-overlay">
          <div className="note-menu-modal">
            <div className="note-menu-header">
              <h3>Note Options</h3>
              <button className="note-menu-close" onClick={() => setShowNoteMenu(false)}>×</button>
            </div>
            <div className="note-menu-content">
              <div className="menu-item" onClick={() => handleMenuAction('moveUp')}>
                <span>⬆️</span> Move Up
              </div>
              <div className="menu-item" onClick={() => handleMenuAction('moveDown')}>
                <span>⬇️</span> Move Down
              </div>
              <div className="menu-item" onClick={() => handleMenuAction('rename')}>
                <span>✏️</span> Rename
              </div>
              <div className="menu-item" onClick={() => handleMenuAction('delete')}>
                <span>🗑️</span> Delete
              </div>
              {notes[selectedNoteIndex]?.isPrivate ? (
                <div className="menu-item" onClick={() => handleMenuAction('makePublic')}>
                  <span>🔓</span> Make Public
                </div>
              ) : (
                <div className="menu-item" onClick={() => handleMenuAction('makePrivate')}>
                  <span>🔒</span> Make Private
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Formatting Popup */}
      <FormattingPopup
        editor={currentEditor}
        isOpen={showFormattingPopup}
        onClose={handleCloseFormattingPopup}
      />

      {/* Exit Confirmation Modal */}
      {showExitConfirmation && (
        <div className="exit-confirmation-overlay">
          <div className="exit-confirmation">
            <h3>Exit Notes</h3>
            <p>Are you sure you want to exit?</p>
            <div className="modal-buttons">
              <button onClick={handleExitConfirm}>Yes</button>
              <button onClick={handleExitCancel}>No</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default App;